import { useSdk, isOidcBasedProvider } from "@fondationcss/sdk";
import type { IFetchWrapper, UnavailableRange, UserAvailability } from "@fondationcss/sdk";
import type { ExamRequest, ExamResponse, ExamsResponse } from "./dto";
import type { ArchiveImpactResponse } from "./dto/archive-impact";
import type { TeacherTransferAssignment, TeacherTransferInventoryResponse } from "./dto/teacher-transfer";
import type { ConsolidatedViewResponse } from "./dto/consolidated-view-response";
import type { ExamAssignmentReportRequest } from "./dto/exam-assignment-report-request";
import type { ImportResultDto, JadeRosterImportResultDto, IndividualRosterImportDefaults } from "./dto/export-import-dto";
import type { FrequentationResponse, FrequentationsResponse, PagedFrequentationsResponse } from "./dto/frequentation-response";
import type { FrequentationModuleResultsResponse } from "./dto/frequentation-module-results-response";
import type { LearningModuleRequest } from "./dto/learning-module-request";
import type { LearningModuleResponse } from "./dto/learning-module-response";
import type { LearningModulesResponse } from "./dto/learning-modules-response";
import type { LearningProgramRequest } from "./dto/learning-program-request";
import type { LearningProgramResponse } from "./dto/learning-program-response";
import type { LearningProgramsResponse } from "./dto/learning-programs-response";
import type { ModulePlanResponse, ModulePlansBatchResponse } from "./dto/module-plan-response";
import type { ScheduleAxisRequest, ScheduleAxisResponse } from "./dto/schedule-axis";
import type { TeacherDaySheetResponse } from "./dto/teacher-day-sheet-response";
import type { BulkPresenceResponse } from "./dto/bulk-presence-response";
import type { TeacherDailyReportResponse } from "./dto/teacher-daily-report-response";
import type { PagedGridQuery } from "@fondationcss/ui";
import type { PresenceRequest } from "./dto/presence-request";
import type { PresenceResponse } from "./dto/presence-response";
import type { PresencesByStudentResponse } from "./dto/presences-by-student-response";
import type { StudentJourneyResponse } from "./dto/student-journey-response";
import type { PagedStudentsResponse } from "./dto/student-response";
import type { AvailableTeachersResponse, PagedTeachersResponse, TeacherResponse, TeachersResponse } from "./dto/teacher-response";
import type { TeacherAvailabilitiesResponse, TeacherAvailabilityRequest, TeacherAvailabilityResponse } from "./dto/teacher-availability";
import type { AteEnterprise, AteEnterpriseContact } from "./model/ate-enterprise";
import type { AteHAC, AteHACEntry } from "./model/ate-hac";
import type { AteActivityAssessment, AteWorkPlacementActivity, AteWorkPlacementActivityNote } from "./model/ate-journal";
import type { AteQuestionnaire } from "./model/ate-questionnaire";
import type { AteActivityTemplate, AteAssessmentRatingGroup, AteHACItem, AteHACTemplate, AteQuestionnaireItemTemplate, AteQuestionnaireTemplate } from "./model/ate-reference-data";
import type { AteWorkPlacementVisit, AteWorkPlacementVisitNote } from "./model/ate-visit";
import type { AteWorkPlacement, AteWorkPlacementDashboardItem, AteWorkPlacementDashboardSummary, AteWorkPlacementNote } from "./model/ate-work-placement";
import type { AteStudentJournalEntry, AteStudentJournalRequest, AteStudentStageDetail, AteStudentStageSummary } from "./model/ate-student-portal";
import type { Exam } from "./model/exam";
import type { ExamListItem } from "./model/exam-list-item";
import type { StudentNote, StudentSharedNote } from './model/student-note';
import type { CreateFrequentationNoteTypeDefinitionRequest, FrequentationNoteTypeDefinition, FrequentationNoteTypeDefinitionsResponse, UpdateFrequentationNoteTypeDefinitionRequest } from './model/frequentation-note-type-definition';
import type { CreateFrequentationTagRequest, FrequentationTag, FrequentationTagAssignmentsResponse, FrequentationTagsResponse, FrequentationTagValueRequest, UpdateFrequentationTagRequest } from './model/frequentation-tag';
import type { GricsFrequentationImportResult } from './model/frequentation';
import type { GricsStudentPreviewRow, GricsFrequentationPreviewRow } from './model/grics-import';
import type {
    ActiveGroupsResponse,
    CreateGroupRequest,
    DuplicateGroupRequest,
    GroupResponse,
    GroupsResponse,
    UpdateGroupRequest
} from "./model/group";
import type { GroupDashboardStats } from "./model/group-dashboard-stats";
import type {
    FrequentationSummary,
    FrequentationSummaryOptions,
    PresenceSummary,
    PresenceSummaryOptions,
    TeacherLoadSummary,
    TeacherLoadSummaryOptions
} from "./model/teaching-summary";
import type { GroupMemberResponse, GroupMembersByProgramResponse, GroupMembersResponse } from "./model/group-membership";
import type { GroupStatsResponse } from "./model/group-stats";
import type { CreateRoomRequest, Room, UpdateRoomRequest } from './model/room';
import type { CreateResourceRequest, Resource, SetResourceProgramsRequest, UpdateResourceRequest } from './model/resource';
import type {
    CreatePlanningRequest,
    PlanningResponse,
    PlanningsResponse,
    UpdatePlanningRequest
} from "./model/planning";
import type {
    ApplyPlanningSlotsRequest,
    ApplyPlanningSlotsResponse,
    AssignmentBusyRequest,
    AssignmentBusyResponse,
    AssignmentConflictsRequest,
    AssignmentConflictsResponse,
    ClearSlotAssignmentsRequest,
    PatchPlanningSlotRequest,
    PatchPlanningSlotsRequest,
    PlanningSlot,
    PlanningSlotsResponse,
    UpdatePlanningSlotsRequest
} from "./model/planning-slot";
import type { EffectivePlanResponse } from "./dto/effective-plan-response";
import type { Presence } from "./model/presence";
import type {
    ScheduleChainInfo,
    ScheduleChainOperation,
    ScheduleChainOperationResult,
    ScheduleChainOwnerType
} from "./model/schedule-personalization";
import type { CreatePresenceTypeDefinitionRequest, PresenceTypeDefinition, PresenceTypeDefinitionsResponse, UpdatePresenceTypeDefinitionRequest } from './model/presence-type-definition';
import type { CreatePresenceStatusDefinitionRequest, PresenceStatusDefinition, PresenceStatusDefinitionsResponse, UpdatePresenceStatusDefinitionRequest } from './model/presence-status-definition';
import type {
    ProgramModuleSequencesResponse,
    UpdateProgramModuleSequencesRequest
} from "./model/program-module-sequence";
import type {
    ProgramPhasesResponse,
    UpdateProgramPhasesRequest,
    PlanningPhasesResponse,
    UpdatePlanningPhasesRequest
} from "./model/program-phase";
import type { CertificationStarted, CertificationStatus, SolveGroupsRequest } from './model/scheduling';
import type { SchedulingV2Request, SchedulingV2Response, DatasetPresetV2 } from './model/scheduling-v2';
import type { CreateSiteRequest, GricsCentreImportResult, Site, UpdateSiteRequest } from './model/site';
import type { GricsTeacherImportResult } from './model/teacher';
import type { CreateExamLocationRequest, ExamLocation, UpdateExamLocationRequest } from './model/exam-location';
import type { InitializationStatusResponse } from './model/initialization-status';
import type { Student, UpdateStudentCoordinatesRequest, GricsStudentImportResult } from "./model/student";
import type { StudentAddress } from "./model/student-address";
import type {
    AddSubmissionFilesRequest,
    AddSubmissionFilesResponse,
    CreateAbsenceJustificationRequest,
    CreateExamSubmissionRequest,
    CreateSubmissionResponse,
    PresenceSubmissionsResponse,
    StudentSubmissionResponse,
    StudentSubmissionsResponse,
    SubmissionKind
} from "./model/student-submission";
import type { UserPreference } from './model/user-preference';
import type { CreateVacancyRequest, VacanciesResponse } from './dto/vacancies';

/** Per-call request options (e.g. a custom errorHandler) forwarded to the underlying fetch wrapper. */
export type SdkRequestOptions = NonNullable<Parameters<IFetchWrapper['post']>[2]>;

export interface DemonstrationConfigurationResponse {
    id: string;
    name: string;
    configurationJson: string;
}

export interface GeneratedPersonResponse {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface ApplyConfigurationResult {
    success: boolean;
    errorMessage: string | null;
    studentsCreated: number;
    students: GeneratedPersonResponse[];
    teachersCreated: number;
    teachers: GeneratedPersonResponse[];
    programsActivated: number;
    activatedProgramNames: string[];
    groupsCreated: number;
    groups: { id: string; name: string; programName: string; startDate: string }[];
    skippedStarts: string[];
    presencesCreated: number;
    centerCreated: number;
    roomsCreated: number;
    calendarsCreated: number;
    /** Working frames given to the created teachers in the Fondation (#923). */
    workFramesCreated: number;
    /** Competence offerings declared for the created teachers (#923). */
    offeringsCreated: number;
    schedulingSolved: boolean | null;
    schedulingSolutionStats: Record<string, unknown> | null;
}

export interface CleanupResult {
    success: boolean;
    errorMessage: string | null;
    rowsDeleted: Record<string, number>;
}

export interface RemoveConfigurationResult {
    success: boolean;
    errorMessage: string | null;
    programsDisabled: string[];
    presencesDeleted: number;
    examsDeleted: number;
    frequentationsDeleted: number;
    groupsDeleted: number;
    planningsDeleted: number;
    teachersDeleted: number;
    teachersUnassigned: number;
    /** Competence offerings removed with the programs (#923). */
    offeringsDeleted: number;
    /** Working frames that went with the deleted teachers (#923). */
    workFramesDeleted: number;
    sitesDeleted: number;
    roomsDeleted: number;
    calendarsDeleted: number;
    skipped: string[];
}

export interface DataStatsResult {
    students: number;
    teachers: number;
    groups: number;
    programs: number;
    activePrograms: number;
    frequentations: number;
    presences: number;
    exams: number;
    sites: number;
    users: number;
    ateWorkPlacements: number;
}

const DEFAULT_SEARCH_MAX_RESULTS = 15;

function buildPagedParams(query: PagedGridQuery): URLSearchParams {
    const params = new URLSearchParams();
    params.set('page', String(query.page));
    params.set('pageSize', String(query.pageSize));
    if (query.search) params.set('search', query.search);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDesc) params.set('sortDesc', 'true');
    if (query.groupBy) params.set('groupBy', query.groupBy);
    // Repeated rather than joined: the key may hold a comma — a program is free to be called
    // « Mécanique, option lourde ». ASP.NET binds repeated keys to a string[] (#721).
    for (const key of query.collapsedGroups ?? []) params.append('collapsedGroups', key);
    if (query.jumpToLetter) params.set('jumpToLetter', query.jumpToLetter);
    if (query.filters) {
        for (const [key, value] of Object.entries(query.filters)) {
            if (value != null) params.set(key, value);
        }
    }
    return params;
}

export interface ExamFilters {
    programId?: string;
    groupId?: string;
    moduleId?: string;
    frequentationId?: string;
    teacherId?: string;
    dateFrom?: string;
    dateTo?: string;
    result?: string;
    studentFilter?: string | null;  // JSON-encoded StudentFilterCriteria
}

export const getCustomSdk = () => {

    const sdk = useSdk();

    // LearningProgram API functions
    const learningPrograms = async () => sdk.fetchWrapper.get<LearningProgramsResponse>(`/learningPrograms/`);
    const availableLearningPrograms = async () => sdk.fetchWrapper.get<LearningProgramsResponse>(`/learningPrograms/available`);
    const getLearningProgram = async (id: string) => sdk.fetchWrapper.get<LearningProgramResponse>(`/learningPrograms/${id}`);
    const addLearningProgram = async (data: LearningProgramRequest) => await sdk.fetchWrapper.post<LearningProgramResponse>(`/learningPrograms/`, data);
    const updateLearningProgram = async (id: string, data: LearningProgramRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${id}`, data, options);
    const setLearningProgramAvailability = async (id: string, isAvailable: boolean, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${id}/setAvailability`, { isAvailable }, options);
    const cloneLearningProgram = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<LearningProgramResponse>(`/learningPrograms/${id}/clone`, {}, options);

    const learningModules = async () => sdk.fetchWrapper.get<LearningModulesResponse>(`/learningModules/`);
    const learningModulesByProgram = async (programId: string) => sdk.fetchWrapper.get<LearningModulesResponse>(`/learningPrograms/${programId}/learningModules/`);
    const getAvailableLearningModules = async (
        programId: string,
        options: { frequentationId?: string; date?: string; periodId?: string; teacherId?: string } = {}
    ) => {
        const params = new URLSearchParams({ programId });
        if (options.frequentationId) params.set('frequentationId', options.frequentationId);
        if (options.date) params.set('date', options.date);
        if (options.periodId) params.set('periodId', options.periodId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        return sdk.fetchWrapper.get<LearningModulesResponse>(`/learningModules/available?${params.toString()}`);
    };
    const getLearningModule = async (id: string) => sdk.fetchWrapper.get<LearningModuleResponse>(`/learningModules/${id}`);
    const addLearningModule = async (data: LearningModuleRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<LearningModuleResponse>(`/learningModules/`, data, options);
    const updateLearningModule = async (id: string, data: LearningModuleRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}`, data, options);
    const archiveLearningModule = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}/archive`, {}, options);
    const restoreLearningModule = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}/restore`, {}, options);
    const archivedLearningModulesByProgram = async (programId: string) => sdk.fetchWrapper.get<LearningModulesResponse>(`/learningPrograms/${programId}/learningModules/archived`);

    // Teacher API functions
    const teachers = async () => sdk.fetchWrapper.get<TeachersResponse>(`/teachers/`);
    const getTeacher = async (id: string) => sdk.fetchWrapper.get<TeacherResponse>(`/teachers/${id}`);
    const teachersPaged = async (query: PagedGridQuery): Promise<PagedTeachersResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedTeachersResponse>(`/teachers/paged?${params.toString()}`);
    };
    const getCurrentTeacher = async () => sdk.fetchWrapper.get<{ teacher: TeacherResponse['teacher'] | null }>(`/teachers/current`);
    const getTeacherPlanning = async (teacherId: string) => {
        return sdk.fetchWrapper.get<import('./dto/teacher-planning-response').TeacherPlanningResponse>(`/teachers/planning?teacherId=${teacherId}`);
    };
    const getGroupPlanning = async (groupId: string) => {
        return sdk.fetchWrapper.get<import('./dto/teacher-planning-response').TeacherPlanningResponse>(`/teachers/planning?fallbackGroupId=${groupId}`);
    };
    const getTeachersCurrentActivity = async () =>
        sdk.fetchWrapper.get<import('./dto/teachers-activity-response').TeachersActivityResponse>(`/teachers/currentActivity`);
    const isTeacherGroupMappedToEntraId = async () => sdk.fetchWrapper.get<{ isMappedToEntraId: boolean }>(`/teachers/isTeacherGroupMappedToEntraId`);
    // #722 — ses cours à venir ET les compétences dont il est enseignant désigné.
    const getTeacherArchiveImpact = async (id: string) =>
        sdk.fetchWrapper.get<ArchiveImpactResponse>(`/teachers/${id}/archiveImpact`);
    // #722, the transfer half: the departing teacher's future commitments as per-block choices,
    // then archive+transfer as one transaction (every block must name its replacement).
    const getTeacherTransferInventory = async (id: string) =>
        sdk.fetchWrapper.get<TeacherTransferInventoryResponse>(`/teachers/${id}/transferInventory`);
    const archiveTeacherWithTransfer = async (id: string, transfers: TeacherTransferAssignment[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<void>(`/teachers/${id}/archiveWithTransfer`, { transfers }, options);
    const restoreTeacher = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/teachers/${id}/restore`, {}, options);
    const teachersByProgram = async (programId: string) => sdk.fetchWrapper.get<TeachersResponse>(`/learningPrograms/${programId}/teachers/`);
    const getAvailableTeachers = async (
        programId: string,
        options: { frequentationId?: string; date?: string; periodId?: string; moduleIds?: string[] } = {}
    ) => {
        const params = new URLSearchParams({ programId });
        if (options.frequentationId) params.set('frequentationId', options.frequentationId);
        if (options.date) params.set('date', options.date);
        if (options.periodId) params.set('periodId', options.periodId);
        if (options.moduleIds?.length) params.set('moduleIds', options.moduleIds.join(','));
        return sdk.fetchWrapper.get<AvailableTeachersResponse>(`/teachers/available?${params.toString()}`);
    };
    const addTeacher = async (userId: string) => await sdk.fetchWrapper.post<TeacherResponse>(`/teachers/`, { userId });

    // Vacancies / hires (#495)
    const getVacancies = async () => sdk.fetchWrapper.get<VacanciesResponse>(`/teachers/vacancies`);
    const createVacancy = async (data: CreateVacancyRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<TeacherResponse>(`/teachers/vacancies`, data, options);
    const archiveVacancy = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/vacancies/${id}/archive`, {}, options);
    const hireVacancy = async (id: string, userId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<TeacherResponse>(`/teachers/vacancies/${id}/hire`, { userId }, options);
    const renameVacancy = async (id: string, label: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/vacancies/${id}/rename`, { label }, options);
    const addTeachersFromEntraIdGroup = async (groupId: string) => await sdk.fetchWrapper.post<TeachersResponse>(`/teachers/FromEntraIdGroup`, { groupId });
    const archiveTeacher = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/${id}/archive`, {}, options);
    // Pass null to clear the custom colour and fall back to the email-derived default.
    const setTeacherColor = async (id: string, color: string | null, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/${id}/color`, { color }, options);

    // Teacher-LearningProgram relationship functions
    const getTeacherLearningPrograms = async (teacherId: string) => sdk.fetchWrapper.get<LearningProgramsResponse>(`/teachers/${teacherId}/learningPrograms/`);
    const assignTeacherToProgram = async (teacherId: string, programId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<void>(`/teachers/${teacherId}/learningPrograms/${programId}`, {}, options);
    const removeTeacherFromProgram = async (teacherId: string, programId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.delete<void>(`/teachers/${teacherId}/learningPrograms/${programId}`, options);

    // Teacher-LearningModule relationship functions (module affinity)
    const getTeacherLearningModules = async (teacherId: string) => sdk.fetchWrapper.get<LearningModulesResponse>(`/teachers/${teacherId}/learningModules/`);
    const setTeacherProgramModules = async (teacherId: string, programId: string, moduleIds: string[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/teachers/${teacherId}/learningPrograms/${programId}/modules`, { moduleIds }, options);

    // Teacher availability (#923). The working frame is the Fondation's (Core.Availability); this
    // route resolves it for a teacher on the server, so a teacher reading their own load needs no
    // Fondation permission. Silent handler: the caller words the failure.
    const getTeacherUnavailableRanges = (teacherId: string, from: string, to: string) =>
        sdk.fetchWrapper.get<{ ranges: UnavailableRange[] }>(
            `/teachers/${teacherId}/unavailableRanges?from=${from}&to=${to}`, { errorHandler: null });
    // The frame itself (validity, weekly windows, absences), in the Fondation's own shape.
    const getTeacherWorkAvailability = (teacherId: string) =>
        sdk.fetchWrapper.get<UserAvailability>(`/teachers/${teacherId}/workAvailability`, { errorHandler: null });
    const getTeacherAvailabilities = async (teacherId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<TeacherAvailabilitiesResponse>(`/teachers/${teacherId}/availabilities`, options);
    const createTeacherAvailability = async (teacherId: string, request: TeacherAvailabilityRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<TeacherAvailabilityResponse>(`/teachers/${teacherId}/availabilities`, request, options);
    const updateTeacherAvailability = async (teacherId: string, availabilityId: string, request: TeacherAvailabilityRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<TeacherAvailabilityResponse>(`/teachers/${teacherId}/availabilities/${availabilityId}`, request, options);
    const deleteTeacherAvailability = async (teacherId: string, availabilityId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/teachers/${teacherId}/availabilities/${availabilityId}`, options);

    // Frequentation API functions
    const frequentations = async () => sdk.fetchWrapper.get<FrequentationsResponse>(`/frequentations/`);
    const myFrequentations = async () => sdk.fetchWrapper.get<FrequentationsResponse>(`/frequentations/mine`);
    const myFrequentationPresences = async (frequentationId: string, startDate: string, endDate: string) =>
        sdk.fetchWrapper.get<PresencesByStudentResponse>(`/frequentations/mine/${frequentationId}/presences?startDate=${startDate}&endDate=${endDate}`);
    const myFrequentationLearningModules = async (frequentationId: string) =>
        sdk.fetchWrapper.get<LearningModulesResponse>(`/frequentations/mine/${frequentationId}/learningModules`);
    const myFrequentationModulePlans = async (frequentationId: string, startDate: string, endDate: string) =>
        sdk.fetchWrapper.get<ModulePlansBatchResponse>(`/frequentations/mine/${frequentationId}/modulePlans?startDate=${startDate}&endDate=${endDate}`);
    const generateMyStudentScheduleReport = async (frequentationId: string): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/scheduleReport`, {});
    // The dated calendar (#961), read over a window — unlike the schedule above, which is the
    // weekly cycle and needs no dates.
    const generateMyStudentCalendarReport = async (frequentationId: string, startDate: string, endDate: string): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/calendarReport`, { startDate, endDate });
    // The two nominative reports a student may ask about themselves. The enrolment is named by the
    // route, never by the body — the ownership guard reads it there.
    const generateMyProgressionReport = async (frequentationId: string): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/progressionReport`, {});
    const generateMyAbsenceReport = async (frequentationId: string, startDate: string, endDate: string): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/absenceReport`, { startDate, endDate });
    const myBulkUpsertPresences = async (frequentationId: string, presences: Partial<Presence>[], presenceIdsToDelete: string[] = []) =>
        await sdk.fetchWrapper.post<{ createdCount: number; updatedCount: number; deletedCount: number }>(
            `/frequentations/mine/${frequentationId}/presences/bulk`,
            { presences, presenceIdsToDelete });
    const getFrequentationById = async (id: string) => sdk.fetchWrapper.get<FrequentationResponse>(`/frequentations/${id}`);

    // ── Schedule personalization (calendar v3, #592) ─────────────────────────
    // Owner = frequentation or planning; the mutations go through the Parcours orchestration,
    // which refuses upstream any operation touching an already recorded day.
    /** Route segment per chain-owner kind (mirror of `ScheduleChainOwnerKind`). */
    const scheduleOwnerPath: Record<ScheduleChainOwnerType, string> = {
        programPhase: 'programPhases',
        planningPhase: 'planningPhases',
    };
    const getScheduleChainInfo = async (ownerType: ScheduleChainOwnerType, ownerId: string) =>
        sdk.fetchWrapper.get<ScheduleChainInfo>(`/${scheduleOwnerPath[ownerType]}/${ownerId}/schedule/chain`);
    const applyScheduleChainOperation = async (
        ownerType: ScheduleChainOwnerType, ownerId: string, operation: ScheduleChainOperation, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<ScheduleChainOperationResult>(
            `/${scheduleOwnerPath[ownerType]}/${ownerId}/schedule/operations`, operation, options);
    const frequentationsByTeacher = async (teacherId: string, date?: string) => {
        const params = date ? `?date=${date}` : '';
        return sdk.fetchWrapper.get<FrequentationsResponse>(`/teachers/${teacherId}/frequentations/${params}`);
    };
    const frequentationsByProgram = async (programId: string, options?: { excludeGroupMembers?: boolean }) => {
        const params = new URLSearchParams();
        params.append('excludeGroupMembers', (options?.excludeGroupMembers ?? false).toString());
        return sdk.fetchWrapper.get<FrequentationsResponse>(`/learningprograms/${programId}/frequentations/?${params.toString()}`);
    };
    const getStudentById = async (studentId: string) =>
        sdk.fetchWrapper.get<Student>(`/students/${studentId}`);
    const getStudentByUserId = async (userId: string) =>
        sdk.fetchWrapper.get<Student>(`/students/byUser/${userId}`);
    const frequentationsByStudent = async (studentId: string) =>
        sdk.fetchWrapper.get<FrequentationsResponse>(`/students/${studentId}/frequentations`);
    const addFrequentation = async (
        userId: string,
        learningProgramId: string,
        startDate: string,
        endDate: string | null = null,
        status: string | null = null,
        fundingSource: string | null = null,
        pathType: string | null = null,
        exemptionCode: string | null = null,
        isConcomitant: boolean = false,
        nbHoursFG: number | null = null,
        isSmallGroup: boolean = false,
        /** Calendar family of the individual delivery created with the frequentation
         *  (individual program). Without it, the server reuses the one of the program's
         *  latest delivery, and failing that creates no delivery (#587). */
        calendarId: string | null = null,
        options?: SdkRequestOptions,
    ) => await sdk.fetchWrapper.post<FrequentationResponse>(`/frequentations/`, {
        userId, learningProgramId, startDate, endDate, status, fundingSource, pathType, exemptionCode, isConcomitant, nbHoursFG, isSmallGroup, calendarId,
    }, options);
    const addFrequentationsFromEntraIdGroup = async (groupId: string, learningProgramId: string, startDate: string, endDate: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ importedCount: number; skippedCount: number }>(`/frequentations/FromEntraIdGroup`, { groupId, learningProgramId, startDate, endDate }, options);
    const updateFrequentation = async (id: string, startDate: string, endDate: string | null, status: string, fundingSource: string | null, pathType: string | null, exemptionCode: string | null, isConcomitant: boolean, nbHoursFG: number | null, isIndividual: boolean = false, isSmallGroup: boolean = false, defaultExamLocationId: string | null = null) => await sdk.fetchWrapper.put<FrequentationResponse>(`/frequentations/${id}`, { startDate, endDate, status, fundingSource, pathType, exemptionCode, isConcomitant, nbHoursFG, isIndividual, isSmallGroup, defaultExamLocationId });

    /**
     * The second staff member beside the enseignant-tuteur (#824). Its own route: the screen
     * that carries the field holds none of the enrolment's other values, and
     * `updateFrequentation` replaces them all.
     */
    const updateFrequentationSecondaryStaff = async (id: string, secondaryStaffId: string | null, secondaryStaffRole: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<FrequentationResponse>(`/frequentations/${id}/SecondaryStaff`, { secondaryStaffId, secondaryStaffRole }, options);
    /** Roles already typed in the establishment, for the combobox to offer back. */
    const getSecondaryStaffRoles = async () =>
        await sdk.fetchWrapper.get<{ roles: string[] }>(`/frequentations/SecondaryStaffRoles`);

    const frequentationsPaged = async (query: PagedGridQuery): Promise<PagedFrequentationsResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedFrequentationsResponse>(`/frequentations/paged?${params.toString()}`);
    };

    /**
     * Student summary aggregated on the server side (#724): totals (including "without a tutor")
     * plus breakdowns by site, program, group and tutor. No dates — the counters cover the current
     * state of the records, and the consolidated view has no window anyway.
     */
    const getFrequentationSummary = async (options: FrequentationSummaryOptions = {}) => {
        const params = new URLSearchParams();
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        if (options.status) params.set('status', options.status);
        // Boolean: sent as soon as it is DEFINED, never "if it is true" — see
        // getPresenceSummary.
        if (options.isIndividual !== undefined) params.set('isIndividual', String(options.isIndividual));
        const query = params.toString();
        return sdk.fetchWrapper.get<FrequentationSummary>(`/frequentations/summary${query ? `?${query}` : ''}`);
    };

    // Student API functions
    const studentsPaged = async (query: PagedGridQuery): Promise<PagedStudentsResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedStudentsResponse>(`/students/paged?${params.toString()}`);
    };

    const addStudent = async (userId: string) =>
        await sdk.fetchWrapper.post<{ id: string; user: { id: string; email: string; firstName: string; lastName: string } }>(`/students/`, { userId });

    const archiveStudent = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/students/${id}/archive`, {}, options);

    const restoreStudent = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/students/${id}/restore`, {}, options);

    const updateStudentCoordinates = async (id: string, data: UpdateStudentCoordinatesRequest) =>
        await sdk.fetchWrapper.put<void>(`/students/${id}/coordinates`, data);

    // Student Address functions
    const getStudentAddresses = async (studentId: string): Promise<StudentAddress[]> =>
        await sdk.fetchWrapper.get<StudentAddress[]>(`/students/${studentId}/addresses`);

    const createStudentAddress = async (studentId: string, data: Omit<StudentAddress, 'id' | 'studentId'>): Promise<StudentAddress> =>
        await sdk.fetchWrapper.post<StudentAddress>(`/students/${studentId}/addresses`, data);

    const updateStudentAddress = async (studentId: string, id: string, data: Omit<StudentAddress, 'id' | 'studentId'>): Promise<void> =>
        await sdk.fetchWrapper.put<void>(`/students/${studentId}/addresses/${id}`, data);

    const deleteStudentAddress = async (studentId: string, id: string): Promise<void> =>
        await sdk.fetchWrapper.delete<void>(`/students/${studentId}/addresses/${id}`);

    const searchStudentsWithFrequentation = async (
        query: string,
        options: { programId?: string; groupId?: string; excludeGroupId?: string; onlyWithActiveFrequentation?: boolean; maxResults?: number } = {}
    ): Promise<{ items: Student[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(options.maxResults ?? DEFAULT_SEARCH_MAX_RESULTS) });
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.excludeGroupId) params.set('excludeGroupId', options.excludeGroupId);
        if (options.onlyWithActiveFrequentation) params.set('onlyWithActiveFrequentation', 'true');
        return sdk.fetchWrapper.get<{ items: Student[] }>(`/students/searchWithFrequentation?${params.toString()}`);
    };

    const searchStudents = async (query: string, maxResults = DEFAULT_SEARCH_MAX_RESULTS): Promise<{ items: Student[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(maxResults) });
        return sdk.fetchWrapper.get<{ items: Student[] }>(`/students/search?${params.toString()}`);
    };

    /** System/Entra accounts the student-add search may offer to enroll — staff excluded server-side (#857). */
    const searchEnrollableUsers = async (query: string, maxResults = 10): Promise<{ users: { id: string; email: string; firstName: string; lastName: string }[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(maxResults) });
        return sdk.fetchWrapper.get<{ users: { id: string; email: string; firstName: string; lastName: string }[] }>(`/students/searchEnrollableUsers?${params.toString()}`);
    };

    // Presence functions
    const addPresence = async (data: PresenceRequest) =>
        await sdk.fetchWrapper.post<PresenceResponse>(`/presences/`, data);

    const updatePresence = async (id: string, data: Partial<Presence>) =>
        await sdk.fetchWrapper.put<void>(`/presences/${id}`, data);

    const deletePresence = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/presences/${id}`);

    // `options` lets a caller pass `{ errorHandler: null }` and phrase the refusal itself — the
    // Présences sheet does, because a 403 here has a specific meaning worth explaining (#540/#879).
    // `presences` lists every row the server wrote, with its id: a note is attached to a presence
    // by id, and a row created by this very call has none until the server says so.
    const bulkUpsertPresences = async (presences: Partial<Presence>[], presenceIdsToDelete: string[] = [], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<BulkPresenceResponse>(`/presences/bulk`, { presences, presenceIdsToDelete }, options);

    const getPresencesByDateRange = async (startDate: string, endDate: string, programId?: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        if (programId) params.append('programId', programId);
        return sdk.fetchWrapper.get<PresencesByStudentResponse>(`/presences/dateRange?${params.toString()}`);
    };

    const getTeacherPresencesByDateRange = async (teacherId: string, startDate: string, endDate: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        return sdk.fetchWrapper.get<PresencesByStudentResponse>(`/teachers/${teacherId}/presences/dateRange?${params.toString()}`);
    };

    /**
     * Presence summary aggregated on the server side (#724): totals plus breakdowns by site,
     * program, group and teacher. `from`/`to` are required — every page brings its own window (one
     * day for assiduity and presences, twenty-eight for assignments). The per-teacher scope is
     * applied by the server: a teacher receives THEIR figures.
     */
    const getPresenceSummary = async (options: PresenceSummaryOptions) => {
        const params = new URLSearchParams();
        params.set('from', options.from);
        params.set('to', options.to);
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        // The `if (x)` guard above is right for an optional GUID, but it would swallow `false` on
        // a boolean: the flag is sent as soon as it is DEFINED, never "if it is true". That is
        // exactly the defect that returned a 400 to the work placements page (#723).
        if (options.isIndividual !== undefined) params.set('isIndividual', String(options.isIndividual));
        return sdk.fetchWrapper.get<PresenceSummary>(`/presences/summary?${params.toString()}`);
    };

    /**
     * Summary of the PLANNED load aggregated on the server side (#724): slots laid down, not
     * recorded presences. `from`/`to` are required — the page brings the school year it displays.
     * The per-teacher scope is applied by the server: a teacher receives THEIR load, and therefore
     * has nothing to select to see their own.
     */
    const getTeacherLoadSummary = async (options: TeacherLoadSummaryOptions) => {
        const params = new URLSearchParams();
        params.set('from', options.from);
        params.set('to', options.to);
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        return sdk.fetchWrapper.get<TeacherLoadSummary>(`/plannings/loadSummary?${params.toString()}`);
    };

    // StudentNote functions (notes belong to the student, #445)
    const getStudentNotesByPresence = async (presenceId: string) =>
        sdk.fetchWrapper.get<{ notes: StudentNote[] }>(`/presences/${presenceId}/notes`);

    const getStudentNotes = async (studentId: string) =>
        sdk.fetchWrapper.get<{ notes: StudentNote[] }>(`/students/${studentId}/notes`);

    const createStudentNoteByPresence = async (presenceId: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.post<StudentNote>(`/presences/${presenceId}/notes`, { noteType, level, message });

    const createStudentNote = async (studentId: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.post<StudentNote>(`/students/${studentId}/notes`, { noteType, level, message });

    const updateStudentNote = async (id: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.put<void>(`/studentNotes/${id}`, { noteType, level, message });

    const deleteStudentNote = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/studentNotes/${id}`);

    // StudentSharedNote functions (#822) — the student-visible follow-up space. Staff routes
    // are student-scoped like the staff notes; the student reads through /Mine only.
    const getStudentSharedNotes = async (studentId: string) =>
        sdk.fetchWrapper.get<{ notes: StudentSharedNote[] }>(`/students/${studentId}/sharedNotes`);

    const createStudentSharedNote = async (studentId: string, level: string, message: string) =>
        sdk.fetchWrapper.post<StudentSharedNote>(`/students/${studentId}/sharedNotes`, { level, message });

    const updateStudentSharedNote = async (id: string, level: string, message: string) =>
        sdk.fetchWrapper.put<void>(`/studentSharedNotes/${id}`, { level, message });

    const deleteStudentSharedNote = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/studentSharedNotes/${id}`);

    const mySharedNotes = async () =>
        sdk.fetchWrapper.get<{ notes: StudentSharedNote[] }>(`/studentSharedNotes/mine`);

    // Exam functions
    const exams = async () => sdk.fetchWrapper.get<ExamsResponse>(`/exams/`);

    function buildExamFilterParams(filters: ExamFilters): URLSearchParams {
        const params = new URLSearchParams();
        if (filters.programId) params.append('programId', filters.programId);
        if (filters.groupId) params.append('groupId', filters.groupId);
        if (filters.moduleId) params.append('moduleId', filters.moduleId);
        if (filters.frequentationId) params.append('frequentationId', filters.frequentationId);
        if (filters.teacherId) params.append('teacherId', filters.teacherId);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.result) params.append('result', filters.result);
        if (filters.studentFilter) params.append('studentFilter', filters.studentFilter);
        return params;
    }

    const examsFiltered = async (filters: ExamFilters) => {
        const params = buildExamFilterParams(filters);
        return sdk.fetchWrapper.get<{ exams: ExamListItem[] }>(`/exams/?${params.toString()}`);
    };

    const myExams = async (filters: Pick<ExamFilters, 'moduleId' | 'dateFrom' | 'dateTo' | 'result'>) => {
        const params = buildExamFilterParams(filters);
        return sdk.fetchWrapper.get<{ exams: ExamListItem[] }>(`/exams/mine?${params.toString()}`);
    };

    const examsCount = async (filters: ExamFilters) => {
        const params = buildExamFilterParams(filters);
        return sdk.fetchWrapper.get<{ count: number }>(`/exams/count?${params.toString()}`);
    };

    const getExam = async (id: string) => sdk.fetchWrapper.get<ExamResponse>(`/exams/${id}`);

    const getExamsByFrequentation = async (frequentationId: string) =>
        sdk.fetchWrapper.get<ExamsResponse>(`/frequentations/${frequentationId}/exams`);

    const createExam = async (data: ExamRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<ExamResponse>(`/exams/`, data, options);

    const updateExam = async (id: string, data: Exam, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/exams/${id}`, data, options);

    const deleteExam = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/exams/${id}`, options);

    // Schedule configuration now managed by Fondation Calendar system (sdk.Calendar)

    // Export/Import functions
    const exportLearningData = async (options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.download(`/learningData/export`, options);
    };

    const importLearningData = async (file: File, contentType: string = "application/zip", options?: SdkRequestOptions): Promise<ImportResultDto> => {
        return await sdk.fetchWrapper.upload("/learningData/import", file, contentType, options) as unknown as ImportResultDto;
    };

    // Consolidated view function
    const getConsolidatedView = async (programId: string, includeInactive: boolean = false, teacherId?: string, excludeGroupMembers: boolean = false) => {
        const params = new URLSearchParams();
        params.append('programId', programId);
        params.append('includeInactive', includeInactive.toString());
        params.append('excludeGroupMembers', excludeGroupMembers.toString());
        if (teacherId) {
            params.append('teacherId', teacherId);
        }
        return sdk.fetchWrapper.get<ConsolidatedViewResponse>(`/consolidatedView/?${params.toString()}`);
    };

    // Append the toolbar-selected centre so ReportBrandingService can brand the PDF (#487).
    const withSiteId = (path: string, siteId?: string | null) =>
        siteId ? `${path}?siteId=${encodeURIComponent(siteId)}` : path;

    // Exam assignment report function
    const generateExamAssignmentReport = async (startDate: string, endDate: string, programIds?: string[], studentFilter?: string | null, siteId?: string | null): Promise<Blob> => {
        // Convert dates to ISO format for API
        const startDateISO = new Date(startDate).toISOString();
        const endDateISO = new Date(endDate).toISOString();

        const payload: ExamAssignmentReportRequest = {
            startDate: startDateISO,
            endDate: endDateISO
        };

        if (programIds && programIds.length > 0) {
            payload.programIds = programIds;
        }
        if (studentFilter) payload.studentFilter = studentFilter;

        return await sdk.fetchWrapper.downloadPost(withSiteId('/examAssignmentReport/', siteId), payload);
    };

    // Absence report function
    const generateAbsenceReport = async (frequentationId: string, startDate: string, endDate: string, siteId?: string | null): Promise<Blob> => {
        // Convert dates to ISO format for API
        const startDateISO = new Date(startDate).toISOString();
        const endDateISO = new Date(endDate).toISOString();

        const payload = {
            frequentationId,
            startDate: startDateISO,
            endDate: endDateISO
        };

        return await sdk.fetchWrapper.downloadPost(withSiteId('/absenceReport/', siteId), payload);
    };

    // Weekly presence report function
    const generateWeeklyPresenceReport = async (teacherId: string, startDate: string, endDate: string, siteId?: string | null): Promise<Blob> => {
        // Convert dates to ISO format for API
        const startDateISO = new Date(startDate).toISOString();
        const endDateISO = new Date(endDate).toISOString();

        const payload = {
            teacherId,
            startDate: startDateISO,
            endDate: endDateISO
        };

        return await sdk.fetchWrapper.downloadPost(withSiteId('/weeklyPresenceReport/', siteId), payload);
    };

    const generateProgressionReport = async (frequentationId: string, siteId?: string | null): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/progressionReport/', siteId), { frequentationId });
    };

    const generateResultTransmissionModuleReport = async (frequentationId: string): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/resultTransmissionModuleReport/', { frequentationId });
    };

    const generateResultTransmissionAllModulesReport = async (groupId: string): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/resultTransmissionAllModulesReport/', { groupId });
    };

    const generateDepartureSheetReport = async (frequentationId: string): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/departureSheetReport/', { frequentationId });
    };

    const generateReturnSheetReport = async (frequentationId: string): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/returnSheetReport/', { frequentationId });
    };

    const generateRetakeRefusalReport = async (frequentationId: string): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/retakeRefusalReport/', { frequentationId });
    };

    const generateFrequentationInfoReport = async (frequentationId: string, siteId?: string | null): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/frequentationInfoReport/', siteId), { frequentationId });
    };

    const generateStudentScheduleReport = async (frequentationId: string, siteId?: string | null): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/studentScheduleReport/', siteId), { frequentationId });
    };

    const generateAtRiskReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, teacherId?: string, studentFilter?: string | null, siteId?: string | null): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (teacherId) payload.teacherId = teacherId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/atRiskReport/', siteId), payload);
    };

    const generateModulePerformanceReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, studentFilter?: string | null, siteId?: string | null): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/modulePerformanceReport/', siteId), payload);
    };

    const generateTeacherWorkloadReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, studentFilter?: string | null, siteId?: string | null): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/teacherWorkloadReport/', siteId), payload);
    };

    const generateTeacherAnnualLoadReport = async (startDate: string, endDate: string, teacherId: string, programId?: string, siteId?: string | null): Promise<Blob> => {
        const payload: Record<string, unknown> = { startDate, endDate, teacherId };
        if (programId) payload.programId = programId;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/teacherAnnualLoadReport/', siteId), payload);
    };

    const generateTeacherAssignmentCalendarReport = async (startDate: string, endDate: string, programId?: string, teacherId?: string, siteId?: string | null): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (teacherId) payload.teacherId = teacherId;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/teacherAssignmentCalendarReport/', siteId), payload);
    };

    const generateCalendarYearReport = async (calendarId: string, calendarYearId: string, siteId?: string | null): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/calendarYearReport/', siteId), {
            calendarId,
            calendarYearId,
        });
    };

    const generateAttendanceTrendReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, teacherId?: string, studentFilter?: string | null, siteId?: string | null): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (teacherId) payload.teacherId = teacherId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/attendanceTrendReport/', siteId), payload);
    };

    const generateCumulativeHoursReport = async (programId: string, siteId?: string | null): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/cumulativeHoursReport/', siteId), { programId });
    };

    const generateWorkStudyTimeReport = async (programId: string, siteId?: string | null): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/workStudyTimeReport/', siteId), { programId });
    };

    // Student journey function
    const getStudentJourney = async (userId: string) => {
        return sdk.fetchWrapper.get<StudentJourneyResponse>(`/studentJourney/${userId}`, { errorHandler: null });
    };

    // FrequentationModuleResults function
    const getFrequentationModuleResults = async (frequentationId: string) =>
        sdk.fetchWrapper.get<FrequentationModuleResultsResponse>(`/frequentations/${frequentationId}/moduleResults`);

    // FrequentationModuleStatus functions
    const getFrequentationModuleStatuses = async (frequentationId: string) =>
        sdk.fetchWrapper.get<{ statuses: Array<{ frequentationId: string; learningModuleId: string; status: string }> }>(`/frequentations/${frequentationId}/moduleStatuses`);

    const setModuleStatus = async (frequentationId: string, moduleId: string, status: string) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/moduleStatuses/${moduleId}`, { status });

    const deleteModuleStatus = async (frequentationId: string, moduleId: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentations/${frequentationId}/moduleStatuses/${moduleId}`);

    const getMyFrequentationModuleLocks = async (frequentationId: string) =>
        sdk.fetchWrapper.get<{ lockedModuleIds: string[] }>(`/frequentations/mine/${frequentationId}/moduleLocks`);

    const getFrequentationModuleLocks = async (frequentationId: string) =>
        sdk.fetchWrapper.get<{ lockedModuleIds: string[] }>(`/frequentations/${frequentationId}/moduleLocks`);

    const setFrequentationModuleLock = async (frequentationId: string, moduleId: string, isLocked: boolean, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/moduleLocks/${moduleId}`, { isLocked }, options);

    const setAllFrequentationModuleLocks = async (frequentationId: string, isLocked: boolean, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/moduleLocks`, { isLocked }, options);

    // Module planning functions
    const getModulePlan = async (frequentationId: string, startDate: string, endDate: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        return sdk.fetchWrapper.get<ModulePlanResponse>(`/frequentations/${frequentationId}/modulePlan?${params.toString()}`);
    };

    const getModulePlansBatch = async (frequentationIds: string[], startDate: string, endDate: string) =>
        sdk.fetchWrapper.post<ModulePlansBatchResponse>(`/modulePlans/batch`, { frequentationIds, startDate, endDate });

    /**
     * The schedule axis — periods and holidays — of a batch of enrolments and/or deliveries
     * over a window (#592). The single source every grid reads: the server applies the
     * student-grain precedence (personalized chain, else the phase of the delivery in effect)
     * so no screen has to resolve chains itself.
     */
    const getScheduleAxis = async (request: ScheduleAxisRequest) =>
        sdk.fetchWrapper.post<ScheduleAxisResponse>(`/schedule/axis`, {
            frequentationIds: request.frequentationIds ?? [],
            planningIds: request.planningIds ?? [],
            from: request.from,
            to: request.to,
        });

    const getTeacherModulePlans = async (teacherId: string, date: string) =>
        sdk.fetchWrapper.get<ModulePlansBatchResponse>(`/teachers/${teacherId}/modulePlans?date=${date}`);

    /**
     * The fully-assembled Présences sheet for a teacher on a day: ordered period columns and,
     * per column, the student rows already matched into them. Replaces the client-side stitching
     * of frequentations + presences + module plans (see TeacherDaySheetService). The rows whose
     * competence sits outside the recording teacher's affinities come back flagged « en soutien »
     * (#813).
     */
    const getTeacherDaySheet = async (teacherId: string, date: string, programId?: string, options?: SdkRequestOptions) => {
        const params = new URLSearchParams();
        params.append('date', date);
        if (programId) params.append('programId', programId);
        return sdk.fetchWrapper.get<TeacherDaySheetResponse>(`/teachers/${teacherId}/daySheet?${params.toString()}`, options);
    };

    /**
     * The same sheet for a whole program with no teacher selected (#813): every student of the
     * program — or of every individualized program under the « Tous les individualisés »
     * pseudo-id (#815) — whatever the teacher. Staff only; the server answers 403 otherwise.
     */
    const getProgramDaySheet = async (
        programId: string, date: string, options?: SdkRequestOptions, siteId?: string,
    ) => {
        const params = new URLSearchParams();
        params.append('date', date);
        // #814: the toolbar's Centre filter. Sent only when it narrows something, so a sheet
        // taken with no centre keeps its URL — and its entry in the offline store, which is
        // keyed on the request.
        if (siteId) params.append('siteId', siteId);
        return sdk.fetchWrapper.get<TeacherDaySheetResponse>(`/learningPrograms/${programId}/daySheet?${params.toString()}`, options);
    };

    /** #817: ONE bell notification inviting the student to choose a competency for every
     *  scheduled period of the day left unchosen. Speaks only — no automatism sits behind it. */
    const notifyUnchosenPeriod = async (frequentationId: string, date: string, periodNames: string[]) => {
        return sdk.fetchWrapper.post<void>(`/frequentations/${frequentationId}/notifyUnchosenPeriod`, {
            date, periodNames,
        });
    };

    const getTeacherDailyReport = async (teacherId: string, date: string, programId?: string) => {
        const params = new URLSearchParams();
        params.append('date', date);
        if (programId) params.append('programId', programId);
        return sdk.fetchWrapper.get<TeacherDailyReportResponse>(`/teachers/${teacherId}/dailyReport?${params.toString()}`);
    };

    // Director Dashboard functions
    // The management dashboard's four cards all take the toolbar's Centre and Programme. Omitted
    // params keep the whole-school reading, so nothing else that calls these routes changes.
    const dashboardScopeQuery = (programId?: string, siteId?: string) => {
        const params = new URLSearchParams();
        if (programId) params.set('programId', programId);
        if (siteId) params.set('siteId', siteId);
        return params.toString() ? `?${params}` : '';
    };

    const getStudentsWithRecentAbsences = async (programId?: string, siteId?: string) => {
        return sdk.fetchWrapper.get<{ students: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; absenceCount: number }> }>(`/DirectorDashboard/StudentsWithRecentAbsences${dashboardScopeQuery(programId, siteId)}`);
    };

    const getStudentsExceedingModuleTime = async (programId?: string, siteId?: string) => {
        return sdk.fetchWrapper.get<{ students: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; moduleNumber: string; moduleTitle: string; moduleCode: string; actualHours: number; expectedHours: number; percentageOfExpected: number }> }>(`/DirectorDashboard/StudentsExceedingModuleTime${dashboardScopeQuery(programId, siteId)}`);
    };

    const getStudentsWithDataIssues = async (programId?: string, siteId?: string) => {
        return sdk.fetchWrapper.get<{
            groupMembershipIssues: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; programTitle: string; groupCount: number }>;
            missingTeacherIssues: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; programTitle: string }>;
        }>(`/DirectorDashboard/StudentsWithDataIssues${dashboardScopeQuery(programId, siteId)}`);
    };

    const getCssPresenceStats = async (programId?: string, siteId?: string) => {
        const query = dashboardScopeQuery(programId, siteId);
        return sdk.fetchWrapper.get<{ presenceStats: Record<string, number> }>(`/DirectorDashboard/PresenceStats${query}`);
    };

    // Planning API functions
    const getPlannings = async () => sdk.fetchWrapper.get<PlanningsResponse>(`/plannings/`);
    const getPlanning = async (id: string) => sdk.fetchWrapper.get<PlanningResponse>(`/plannings/${id}`);
    const createPlanning = async (data: CreatePlanningRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<PlanningResponse>(`/plannings/`, data, options);
    const updatePlanning = async (id: string, data: UpdatePlanningRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/plannings/${id}`, data, options);
    const deletePlanning = async (id: string) => await sdk.fetchWrapper.delete<void>(`/plannings/${id}`);

    // Group API functions
    const groups = async () => sdk.fetchWrapper.get<GroupsResponse>(`/groups/`);
    const getActiveGroups = async () => sdk.fetchWrapper.get<ActiveGroupsResponse>(`/groups/active`);
    const getGroup = async (id: string) => sdk.fetchWrapper.get<GroupResponse>(`/groups/${id}`);
    const createGroup = async (data: CreateGroupRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<GroupResponse>(`/groups/`, data, options);
    const duplicateGroup = async (id: string, data: DuplicateGroupRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<GroupResponse>(`/groups/${id}/duplicate`, data, options);
    const updateGroup = async (id: string, data: UpdateGroupRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/groups/${id}`, data, options);
    const deleteGroup = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.delete<void>(`/groups/${id}`, options);

    // Site (Centre) API functions
    const getSites = async (includeArchived = false) =>
        sdk.fetchWrapper.get<{ sites: Site[] }>(`/sites/${includeArchived ? '?includeArchived=true' : ''}`);
    const getSite = async (id: string) => sdk.fetchWrapper.get<{ site: Site }>(`/sites/${id}`);
    const createSite = async (data: CreateSiteRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<{ site: Site }>(`/sites/`, data, options);
    const updateSite = async (data: UpdateSiteRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/sites/`, data, options);
    const setProgramSites = async (programId: string, siteIds: string[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/sites`, { siteIds }, options);
    // Overwrites local edits on GRICS-linked centres — the confirmation lives in the dialog that
    // calls this, not here (see sites.vue's import dialog).
    const importGricsCentres = async (options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<GricsCentreImportResult>(`/gricsImport/centres`, {}, options);
    // Overwrites local edits on GRICS-linked teachers and reactivates archived ones — the
    // confirmation lives in the dialog that calls this, not here (see teachers.vue's import dialog).
    const importGricsTeachers = async (options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<GricsTeacherImportResult>(`/gricsImport/teachers`, {}, options);
    // Overwrites local edits on GRICS-linked students and reactivates archived ones — the
    // confirmation lives in the dialog that calls this, not here (see students.vue's import dialog).
    // allowCached: true reuses the GRICS snapshot the dialog already previewed instead of paying
    // for the whole per-student fan-out again (moments after the preview call already did it).
    const importGricsStudents = async (allowCached = false, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<GricsStudentImportResult>(`/gricsImport/students?allowCached=${allowCached}`, {}, options);
    // Implicitly imports FP students first, then their frequentations — see
    // GricsFrequentationImportService. The confirmation lives in the dialog that calls this, not
    // here (see enrollments.vue's import dialog). allowCached: see importGricsStudents.
    const importGricsFrequentations = async (allowCached = false, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<GricsFrequentationImportResult>(`/gricsImport/frequentations?allowCached=${allowCached}`, {}, options);
    // Read-only — shows what importGricsStudents would do before the user commits to it.
    const previewGricsStudents = async (allowCached = false, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.get<GricsStudentPreviewRow[]>(`/gricsImport/students/preview?allowCached=${allowCached}`, options);
    // Read-only — shows what importGricsFrequentations would do before the user commits to it.
    // allowCached: true reuses the last GRICS snapshot — pass it when reloading the SAME review
    // session (e.g. right after making one flagged program available), not on the initial open.
    const previewGricsFrequentations = async (allowCached = false, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.get<GricsFrequentationPreviewRow[]>(`/gricsImport/frequentations/preview?allowCached=${allowCached}`, options);

    // Exam locations (#823) — master list of exam venues, managed in Gestion → Établissement.
    const getExamLocations = async (includeArchived = false) =>
        sdk.fetchWrapper.get<{ locations: ExamLocation[] }>(`/examLocations/${includeArchived ? '?includeArchived=true' : ''}`);
    const createExamLocation = async (data: CreateExamLocationRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ location: ExamLocation }>(`/examLocations/`, data, options);
    const updateExamLocation = async (data: UpdateExamLocationRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/examLocations/`, data, options);
    /** Archive (soft delete): exams and defaults keep referencing the venue; pickers stop offering it. */
    const archiveExamLocation = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/examLocations/${id}`, options);

    // Room API functions
    const getRooms = async (includeArchived = false) =>
        sdk.fetchWrapper.get<{ rooms: Room[] }>(`/rooms/${includeArchived ? '?includeArchived=true' : ''}`);
    const getRoomsBySite = async (siteId: string) => sdk.fetchWrapper.get<{ rooms: Room[] }>(`/sites/${siteId}/rooms`);
    const getRoom = async (id: string) => sdk.fetchWrapper.get<{ room: Room }>(`/rooms/${id}`);
    const createRoom = async (data: CreateRoomRequest) => await sdk.fetchWrapper.post<{ room: Room }>(`/rooms/`, data);
    const updateRoom = async (data: UpdateRoomRequest) => await sdk.fetchWrapper.put<void>(`/rooms/`, data);
    // #777 — lu AVANT d'archiver, pour que la confirmation nomme ce qu'elle engage.
    const getRoomArchiveImpact = async (id: string) =>
        sdk.fetchWrapper.get<ArchiveImpactResponse>(`/rooms/${id}/archiveImpact`);

    // Resource API functions
    const getResources = async (includeArchived = false) =>
        sdk.fetchWrapper.get<{ resources: Resource[] }>(`/resources/${includeArchived ? '?includeArchived=true' : ''}`);
    const getResourcesBySite = async (siteId: string) => sdk.fetchWrapper.get<{ resources: Resource[] }>(`/sites/${siteId}/resources`);
    const getResource = async (id: string) => sdk.fetchWrapper.get<{ resource: Resource }>(`/resources/${id}`);
    const createResource = async (data: CreateResourceRequest) => await sdk.fetchWrapper.post<{ resource: Resource }>(`/resources/`, data);
    const updateResource = async (data: UpdateResourceRequest) => await sdk.fetchWrapper.put<void>(`/resources/`, data);
    const setResourcePrograms = async (id: string, data: SetResourceProgramsRequest) => await sdk.fetchWrapper.put<void>(`/resources/${id}/programs`, data);

    // UserPreference API functions
    const getUserPreferences = async () => sdk.fetchWrapper.get<UserPreference>(`/userPreferences`);
    const updateUserPreferences = async (defaultSiteId: string | null) =>
        await sdk.fetchWrapper.put<void>(`/userPreferences`, { defaultSiteId });

    // Shared authenticated multipart upload for logos — mirrors uploadRosterFile's auth handling
    // (fetchWrapper.upload throws for OIDC providers).
    const uploadLogoMultipart = async (path: string, file: File): Promise<void> => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        const headers: Record<string, string> = {};
        const init: RequestInit = { method: 'POST', headers, body: formData };
        if (isOidcBasedProvider(sdk.selectedAuthProvider.value)) {
            init.credentials = 'include';
        } else if (!sdk.isAnonymous.value) {
            if (sdk.isTokenAuth.value) {
                headers['Authorization'] = `Token ${sdk.apiToken.value}`;
            } else {
                const token = await sdk.getApiAccessToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            }
        }
        const apiUrl = sdk.settings.value?.apiUrl ?? '';
        const response = await fetch(`${apiUrl}${path}`, init);
        if (!response.ok) {
            const text = await response.text();
            let message = response.statusText;
            try { message = (JSON.parse(text)?.error) ?? message; } catch { /* keep statusText */ }
            throw new Error(message);
        }
    };
    // Per-centre logo (#487)
    const getSiteLogo = async (id: string) =>
        sdk.fetchWrapper.get<{ dataUrl: string | null }>(`/sites/${id}/logo`, { errorHandler: null });
    const uploadSiteLogo = (id: string, file: File) => uploadLogoMultipart(`/sites/${id}/logo`, file);
    const deleteSiteLogo = async (id: string) => await sdk.fetchWrapper.delete<void>(`/sites/${id}/logo`);

    // FrequentationTag API functions
    const getFrequentationTags = async () => sdk.fetchWrapper.get<FrequentationTagsResponse>(`/frequentationTags`);
    const createFrequentationTag = async (data: CreateFrequentationTagRequest) =>
        await sdk.fetchWrapper.post<{ tag: FrequentationTag }>(`/frequentationTags`, data);
    const updateFrequentationTag = async (id: string, data: UpdateFrequentationTagRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationTags/${id}`, data);
    const deleteFrequentationTag = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationTags/${id}`);
    const addFrequentationTagValue = async (tagId: string, data: FrequentationTagValueRequest) =>
        await sdk.fetchWrapper.post<{ id: string; name: string }>(`/frequentationTags/${tagId}/values`, data);
    const updateFrequentationTagValue = async (tagId: string, valueId: string, data: FrequentationTagValueRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationTags/${tagId}/values/${valueId}`, data);
    const deleteFrequentationTagValue = async (tagId: string, valueId: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationTags/${tagId}/values/${valueId}`);
    const getTagsByStudent = async (studentId: string) =>
        sdk.fetchWrapper.get<FrequentationTagAssignmentsResponse>(`/students/${studentId}/tags`);
    const setStudentTags = async (studentId: string, valueIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/students/${studentId}/tags`, { valueIds });

    // FrequentationNoteTypeDefinition API functions
    const getFrequentationNoteTypeDefinitions = async () =>
        sdk.fetchWrapper.get<FrequentationNoteTypeDefinitionsResponse>(`/frequentationNoteTypeDefinitions`);
    const createFrequentationNoteTypeDefinition = async (data: CreateFrequentationNoteTypeDefinitionRequest) =>
        await sdk.fetchWrapper.post<FrequentationNoteTypeDefinition>(`/frequentationNoteTypeDefinitions`, data);
    const updateFrequentationNoteTypeDefinition = async (id: string, data: UpdateFrequentationNoteTypeDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationNoteTypeDefinitions/${id}`, data);
    const deleteFrequentationNoteTypeDefinition = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationNoteTypeDefinitions/${id}`);
    const reorderFrequentationNoteTypeDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/frequentationNoteTypeDefinitions/Reorder`, { orderedIds });

    // PresenceTypeDefinition API functions
    const getPresenceTypeDefinitions = async () =>
        sdk.fetchWrapper.get<PresenceTypeDefinitionsResponse>(`/presenceTypeDefinitions`);
    const createPresenceTypeDefinition = async (data: CreatePresenceTypeDefinitionRequest) =>
        await sdk.fetchWrapper.post<PresenceTypeDefinition>(`/presenceTypeDefinitions`, data);
    const updatePresenceTypeDefinition = async (id: string, data: UpdatePresenceTypeDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/presenceTypeDefinitions/${id}`, data);
    const deletePresenceTypeDefinition = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/presenceTypeDefinitions/${id}`);
    const reorderPresenceTypeDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/presenceTypeDefinitions/Reorder`, { orderedIds });

    // PresenceStatusDefinition API functions
    const getPresenceStatusDefinitions = async () =>
        sdk.fetchWrapper.get<PresenceStatusDefinitionsResponse>(`/presenceStatusDefinitions`);
    const createPresenceStatusDefinition = async (data: CreatePresenceStatusDefinitionRequest) =>
        await sdk.fetchWrapper.post<PresenceStatusDefinition>(`/presenceStatusDefinitions`, data);
    const updatePresenceStatusDefinition = async (id: string, data: UpdatePresenceStatusDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/presenceStatusDefinitions/${id}`, data);
    const deletePresenceStatusDefinition = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/presenceStatusDefinitions/${id}`);
    const reorderPresenceStatusDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/presenceStatusDefinitions/Reorder`, { orderedIds });

    // Group Membership API functions
    const getGroupMembers = async (groupId: string) => sdk.fetchWrapper.get<GroupMembersResponse>(`/groups/${groupId}/members`);
    const getAvailableGroupMembers = async (groupId: string) => sdk.fetchWrapper.get<FrequentationsResponse>(`/groups/${groupId}/availableMembers`);
    const addGroupMember = async (groupId: string, frequentationId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<GroupMemberResponse>(`/groups/${groupId}/members`, { frequentationId }, options);
    const removeGroupMember = async (groupId: string, frequentationId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.delete<void>(`/groups/${groupId}/members/${frequentationId}`, options);
    // The SDK's fetchWrapper.upload() unconditionally calls getApiAccessToken(), which throws
    // for OIDC providers. We replicate the auth pattern the fetch wrapper applies
    // (credentials: 'include' for OIDC, Bearer/Token for the others) and post FormData
    // so the backend can read it via HttpRequest.ReadFormAsync().
    const uploadRosterFile = async (path: string, file: File, fields?: Record<string, string | number | boolean | null | undefined>): Promise<JadeRosterImportResultDto> => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        if (fields) {
            for (const [key, value] of Object.entries(fields)) {
                if (value !== null && value !== undefined) formData.append(key, String(value));
            }
        }
        const headers: Record<string, string> = {};
        const init: RequestInit = { method: 'POST', headers, body: formData };
        if (isOidcBasedProvider(sdk.selectedAuthProvider.value)) {
            init.credentials = 'include';
        } else if (!sdk.isAnonymous.value) {
            if (sdk.isTokenAuth.value) {
                headers['Authorization'] = `Token ${sdk.apiToken.value}`;
            } else {
                const token = await sdk.getApiAccessToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            }
        }
        const apiUrl = sdk.settings.value?.apiUrl ?? '';
        const response = await fetch(`${apiUrl}${path}`, init);
        const text = await response.text();
        if (!response.ok) {
            let message = response.statusText;
            try { message = (JSON.parse(text)?.error) ?? message; } catch { /* keep statusText */ }
            throw new Error(message);
        }
        return text ? JSON.parse(text) as JadeRosterImportResultDto : { totalRows: 0, membersAdded: 0, membersAlreadyExisting: 0, errors: [] };
    };
    const importJadeRoster = (groupId: string, file: File): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/groups/${groupId}/members/importJadeRoster`, file);
    const importAdmissionFpRoster = (groupId: string, file: File): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/groups/${groupId}/members/importAdmissionFpRoster`, file);
    const importIndividualJadeRoster = (programId: string, file: File, defaults: IndividualRosterImportDefaults): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/learningPrograms/${programId}/importIndividualJadeRoster`, file, { ...defaults });
    const importIndividualAdmissionFpRoster = (programId: string, file: File, defaults: IndividualRosterImportDefaults): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/learningPrograms/${programId}/importIndividualAdmissionFpRoster`, file, { ...defaults });

    // ProgramModuleSequence API functions.
    // Program-level GET is a read-only aggregate across all phases (solver / display).
    const getProgramModuleSequences = async (programId: string) => sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/learningPrograms/${programId}/moduleSequences`);
    // Sequences are edited per phase.
    const getProgramPhaseModuleSequences = async (programId: string, phaseId: string) => sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/learningPrograms/${programId}/phases/${phaseId}/moduleSequences`);
    const updateProgramPhaseModuleSequences = async (programId: string, phaseId: string, data: UpdateProgramModuleSequencesRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/phases/${phaseId}/moduleSequences`, data, options);

    // ProgramPhase API functions
    const getProgramPhases = async (programId: string) => sdk.fetchWrapper.get<ProgramPhasesResponse>(`/learningPrograms/${programId}/phases`);
    // Like their delivery-side twins, both template-phase writes refuse with a message in the
    // body (409): the caller passes `{ errorHandler: null }` to silence the SDK's generic bubble
    // and relay that message (see utils/api-error).
    const updateProgramPhases = async (programId: string, data: UpdateProgramPhasesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/phases`, data, options);
    // Deletes one phase; when moveSequencesToPhaseId is given, the phase's module
    // sequences are moved to that phase first (atomically, server-side).
    const deleteProgramPhase = async (programId: string, phaseId: string, moveSequencesToPhaseId?: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/learningPrograms/${programId}/phases/${phaseId}${moveSequencesToPhaseId ? `?moveSequencesToPhaseId=${moveSequencesToPhaseId}` : ''}`, options);

    const getProgramGroupMembers = async (programId: string) => sdk.fetchWrapper.get<GroupMembersByProgramResponse>(`/learningPrograms/${programId}/groupMembers`);

    // Group stats
    const getGroupStats = async (groupId: string) => sdk.fetchWrapper.get<GroupStatsResponse>(`/groups/${groupId}/stats`);
    const getGroupDashboardStats = async (groupId: string, date: string) => sdk.fetchWrapper.get<GroupDashboardStats>(`/groups/${groupId}/dashboardStats?date=${date}`);

    // Delivery-local phase/sequence API — a Planning's own copied tree, editable
    // without affecting the program. Mirrors the program endpoints and reuses the
    // sequence DTO shape (its programPhaseId carries the planning phase id). The phase
    // itself has its own shape: unlike the template, it carries a calendar, and that
    // calendar is mandatory (#587).
    const getPlanningPhases = async (planningId: string) =>
        sdk.fetchWrapper.get<PlanningPhasesResponse>(`/plannings/${planningId}/phases`);
    // Both phase writes refuse with a French message in the body (409): the caller passes
    // `{ errorHandler: null }` to silence the SDK's generic bubble and relay that message
    // (see utils/api-error).
    const updatePlanningPhases = async (planningId: string, data: UpdatePlanningPhasesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/phases`, data, options);
    const deletePlanningPhase = async (planningId: string, phaseId: string, moveSequencesToPhaseId?: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/plannings/${planningId}/phases/${phaseId}${moveSequencesToPhaseId ? `?moveSequencesToPhaseId=${moveSequencesToPhaseId}` : ''}`, options);
    const getPlanningModuleSequences = async (planningId: string) =>
        sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/plannings/${planningId}/moduleSequences`);
    const getPlanningPhaseModuleSequences = async (planningId: string, phaseId: string) =>
        sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/plannings/${planningId}/phases/${phaseId}/moduleSequences`);
    const updatePlanningPhaseModuleSequences = async (planningId: string, phaseId: string, data: UpdateProgramModuleSequencesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/phases/${phaseId}/moduleSequences`, data, options);

    // PlanningSlot API functions (V2 slot-based model)
    const getPlanningSlots = async (planningId: string) =>
        sdk.fetchWrapper.get<PlanningSlotsResponse>(`/plannings/${planningId}/slots`);
    const updatePlanningSlots = async (planningId: string, data: UpdatePlanningSlotsRequest) =>
        await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/slots`, data);
    const patchPlanningSlot = async (planningId: string, slotId: string, data: PatchPlanningSlotRequest) =>
        await sdk.fetchWrapper.patch<PlanningSlot>(`/plannings/${planningId}/slots/${slotId}`, data);
    // Bulk patch: reassign the editable triple of many slots in one request (one
    // fragment = many slots), instead of one PATCH per slot.
    const patchPlanningSlots = async (planningId: string, data: PatchPlanningSlotsRequest) =>
        await sdk.fetchWrapper.patch<{ updated: number }>(`/plannings/${planningId}/slots`, data);
    // #464 — where is this teacher/room already assigned in OTHER groups on these cells?
    const findSlotAssignmentConflicts = async (planningId: string, data: AssignmentConflictsRequest) =>
        await sdk.fetchWrapper.post<AssignmentConflictsResponse>(`/plannings/${planningId}/slots/assignmentConflicts`, data);
    // #464 — release a teacher/room from the listed slots of another planning (move step).
    const clearSlotAssignments = async (planningId: string, data: ClearSlotAssignmentsRequest) =>
        await sdk.fetchWrapper.post<{ updated: number }>(`/plannings/${planningId}/slots/clearAssignments`, data);
    // #464 (review) — every teacher/room already taken elsewhere on these cells (availability pills).
    const findSlotAssignmentBusy = async (planningId: string, data: AssignmentBusyRequest) =>
        await sdk.fetchWrapper.post<AssignmentBusyResponse>(`/plannings/${planningId}/slots/assignmentBusy`, data);
    // Discards the planning's slots and reseeds them (e.g. after a phase calendar change).
    const regeneratePlanningSlots = async (planningId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ reseeded: number }>(`/plannings/${planningId}/slots/regenerate`, {}, options);
    const getEffectivePlan = async (planningId: string, startDate: string, endDate?: string) => {
        const qs = endDate ? `?startDate=${startDate}&endDate=${endDate}` : `?startDate=${startDate}`;
        return sdk.fetchWrapper.get<EffectivePlanResponse>(`/plannings/${planningId}/effectivePlan${qs}`);
    };

    // Individual Planning API functions
    const createIndividualPlanning = async (frequentationId: string, data: { startDate?: string; calendarId?: string | null }) =>
        await sdk.fetchWrapper.post<{ planningId: string; frequentationId: string }>(`/frequentations/${frequentationId}/individualPlanning`, data);
    const deleteIndividualPlanning = async (frequentationId: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentations/${frequentationId}/individualPlanning`);
    const getIndividualPlanningsByProgram = async (programId: string) =>
        sdk.fetchWrapper.get<FrequentationsResponse>(`/learningPrograms/${programId}/individualPlannings`);

    const MODULE_NAME = "Parcours.LearningModule";
    const PERMISSION_ADMINISTRATOR = MODULE_NAME + ".Administrateur";
    const PERMISSION_SUPERVISOR = MODULE_NAME + ".Superviseur";
    const PERMISSION_TEACHER = MODULE_NAME + ".Enseignant";
    // « Enseignement élargi » (#879, phase 1): the « Enseignant chef » role reads every student
    // like a Superviseur, without any of the Superviseur's routes.
    const PERMISSION_EXTENDED_TEACHER = MODULE_NAME + ".EnseignementÉlargi";
    const PERMISSION_ARCHIVE_STUDENTS = MODULE_NAME + ".ArchivageÉlèves";
    const PERMISSION_VIEWER = MODULE_NAME + ".Visiteur";

    // ================================================================
    // ATE (Alternance Travail-Études) API functions
    // ================================================================

    const ATE_MODULE = "Parcours.ATE";
    const ATE_PERMISSION_ADMINISTRATOR = ATE_MODULE + ".Administrateur";
    const ATE_PERMISSION_TEACHER = ATE_MODULE + ".Enseignant";
    const ATE_PERMISSION_SUPERVISOR = ATE_MODULE + ".Superviseur";
    const ATE_PERMISSION_VIEWER = ATE_MODULE + ".Visiteur";

    // ================================================================
    // Demonstration API functions
    // ================================================================

    // Four distinct permissions, specific to the demo engine — see DemonstrationModule.cs.
    // Administrateur carries everything (including the purge); the three others split up the
    // rest, because writing a scenario, applying it to the database and emptying the database
    // are not entrusted to the same people.
    const DEMO_MODULE = "Parcours.Demonstration";
    const DEMO_PERMISSION_ADMINISTRATOR = DEMO_MODULE + ".Administrateur";
    /** Write the configurations and generate fake people. */
    const DEMO_PERMISSION_DESIGNER = DEMO_MODULE + ".Concepteur";
    /** Apply and remove an existing configuration. */
    const DEMO_PERMISSION_PRESENTER = DEMO_MODULE + ".Démonstrateur";
    /** Read-only access to the configurations and to the state of the data. */
    const DEMO_PERMISSION_OBSERVER = DEMO_MODULE + ".Observateur";

    const getDemonstrationConfigurations = async () =>
        sdk.fetchWrapper.get<DemonstrationConfigurationResponse[]>(`/demonstration/configurations`);

    const getDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.get<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}`);

    /**
     * The configuration that covers every screen (#984), computed server-side from this
     * instance's referential. Nothing is persisted: the caller fills its form with it and saves
     * it like any other scenario. Typed by the caller — the shape is `demo.vue`'s `DemoConfig`,
     * which lives with the form it drives rather than in the SDK.
     */
    const getRecommendedDemonstrationConfiguration = async () =>
        sdk.fetchWrapper.get<Record<string, unknown>>(`/demonstration/configurations/recommended`);

    const createDemonstrationConfiguration = async (name: string, configurationJson: string) =>
        sdk.fetchWrapper.post<DemonstrationConfigurationResponse>(`/demonstration/configurations`, { name, configurationJson });

    const updateDemonstrationConfiguration = async (id: string, name: string, configurationJson: string) =>
        sdk.fetchWrapper.put<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}`, { name, configurationJson });

    const deleteDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/demonstration/configurations/${id}`);

    const duplicateDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.post<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}/duplicate`, {});

    const generateDemoStudents = async (count: number) =>
        sdk.fetchWrapper.post<GeneratedPersonResponse[]>(`/demonstration/generate-students`, { count });

    const generateDemoTeachers = async (count: number) =>
        sdk.fetchWrapper.post<GeneratedPersonResponse[]>(`/demonstration/generate-teachers`, { count });

    const applyDemonstrationConfiguration = async (id: string, presenceEndDate?: string) =>
        sdk.fetchWrapper.post<ApplyConfigurationResult>(`/demonstration/configurations/${id}/apply`, { presenceEndDate });

    const removeDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.post<RemoveConfigurationResult>(`/demonstration/configurations/${id}/remove`, {});

    /**
     * Starts an apply in the background and returns the id to follow it by. Progress and the final
     * result arrive on the permanent SSE channel under `demonstration.apply` — see `runJob`.
     */
    const applyDemonstrationConfigAsync = async (id: string, presenceEndDate?: string) =>
        sdk.fetchWrapper.post<{ jobId: string }>(
            `/demonstration/configurations/${id}/apply-async`, { presenceEndDate });

    const cleanupDemoData = async () =>
        sdk.fetchWrapper.post<CleanupResult>(`/demonstration/cleanup`, {});

    const getDemoDataStats = async () =>
        sdk.fetchWrapper.get<DataStatsResult>(`/demonstration/stats`);

    // On-demand optimality certification: the job goes to the Byrnu scheduler
    // (production run), progress polls its status.
    const startSchedulingCertification = async (data: { groupIds: string[] }) =>
        sdk.fetchWrapper.post<CertificationStarted>(`/scheduling-v2/certifications`, data);
    const getSchedulingCertification = async (jobId: string) =>
        sdk.fetchWrapper.get<CertificationStatus>(`/scheduling-v2/certifications/${jobId}`);
    // Asynchronous deep analysis: offered when the instant generation refuses
    // (instance too constrained) — feasible/infeasible verdict + quality.
    const startSchedulingAnalysis = async (data: { groupIds: string[] }) =>
        sdk.fetchWrapper.post<CertificationStarted>(`/scheduling-v2/analyses`, data);
    const getSchedulingAnalysis = async (jobId: string) =>
        sdk.fetchWrapper.get<CertificationStatus>(`/scheduling-v2/analyses/${jobId}`);

    // Reference Data
    const getAteRatingGroups = async () =>
        sdk.fetchWrapper.get<AteAssessmentRatingGroup[]>(`/ate/ratinggroups`);
    const createAteRatingGroup = async (data: Partial<AteAssessmentRatingGroup>, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<AteAssessmentRatingGroup>(`/ate/ratinggroups`, data, options);
    const updateAteRatingGroup = async (id: string, data: Partial<AteAssessmentRatingGroup>, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/ate/ratinggroups/${id}`, data, options);
    const archiveAteRatingGroup = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/ate/ratinggroups/${id}`, options);

    const getAteActivityTemplates = async (moduleId: string) =>
        sdk.fetchWrapper.get<AteActivityTemplate[]>(`/ate/learningmodules/${moduleId}/activitytemplates`);
    const createAteActivityTemplate = async (data: Partial<AteActivityTemplate>) =>
        sdk.fetchWrapper.post<AteActivityTemplate>(`/ate/activitytemplates`, data);
    const updateAteActivityTemplate = async (id: string, data: Partial<AteActivityTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/activitytemplates/${id}`, data);
    const archiveAteActivityTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activitytemplates/${id}`);

    const getAteQuestionnaireTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaireTemplate[]>(`/ate/learningprograms/${programId}/questionnairetemplates`);
    const createAteQuestionnaireTemplate = async (data: Partial<AteQuestionnaireTemplate>) =>
        sdk.fetchWrapper.post<AteQuestionnaireTemplate>(`/ate/questionnairetemplates`, data);
    const updateAteQuestionnaireTemplate = async (id: string, data: Partial<AteQuestionnaireTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/questionnairetemplates/${id}`, data);
    const archiveAteQuestionnaireTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/questionnairetemplates/${id}`);
    const getArchivedAteQuestionnaireTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaireTemplate[]>(`/ate/learningprograms/${programId}/questionnairetemplates/archived`);
    const restoreAteQuestionnaireTemplate = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnairetemplates/${id}/restore`, {});
    const createAteQuestionnaireTemplateItem = async (templateId: string, data: Partial<AteQuestionnaireItemTemplate>) =>
        sdk.fetchWrapper.post<AteQuestionnaireItemTemplate>(`/ate/questionnairetemplates/${templateId}/items`, data);
    const updateAteQuestionnaireTemplateItem = async (templateId: string, itemId: string, data: Partial<AteQuestionnaireItemTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/questionnairetemplates/${templateId}/items/${itemId}`, data);
    const deleteAteQuestionnaireTemplateItem = async (templateId: string, itemId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/questionnairetemplates/${templateId}/items/${itemId}`);

    const getAteHACTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteHACTemplate[]>(`/ate/learningprograms/${programId}/hactemplates`);
    const createAteHACTemplate = async (data: Partial<AteHACTemplate>) =>
        sdk.fetchWrapper.post<AteHACTemplate>(`/ate/hactemplates`, data);
    const updateAteHACTemplate = async (id: string, data: Partial<AteHACTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/hactemplates/${id}`, data);
    const archiveAteHACTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hactemplates/${id}`);
    const createAteHACTemplateItem = async (templateId: string, data: Partial<AteHACItem>) =>
        sdk.fetchWrapper.post<AteHACItem>(`/ate/hactemplates/${templateId}/items`, data);
    const updateAteHACTemplateItem = async (templateId: string, itemId: string, data: Partial<AteHACItem>) =>
        sdk.fetchWrapper.put<void>(`/ate/hactemplates/${templateId}/items/${itemId}`, data);
    const deleteAteHACTemplateItem = async (templateId: string, itemId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hactemplates/${templateId}/items/${itemId}`);

    // Enterprises
    const getAteEnterprises = async () =>
        sdk.fetchWrapper.get<AteEnterprise[]>(`/ate/enterprises`);
    const getAteEnterprise = async (id: string) =>
        sdk.fetchWrapper.get<AteEnterprise>(`/ate/enterprises/${id}`);
    const createAteEnterprise = async (data: Partial<AteEnterprise>) =>
        sdk.fetchWrapper.post<AteEnterprise>(`/ate/enterprises`, data);
    const updateAteEnterprise = async (id: string, data: Partial<AteEnterprise>) =>
        sdk.fetchWrapper.put<void>(`/ate/enterprises/${id}`, data);
    const archiveAteEnterprise = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/enterprises/${id}`);

    // Enterprise Contacts
    const getAteEnterpriseContacts = async (enterpriseId: string) =>
        sdk.fetchWrapper.get<AteEnterpriseContact[]>(`/ate/enterprises/${enterpriseId}/contacts`);
    const getAteEnterpriseContact = async (id: string) =>
        sdk.fetchWrapper.get<AteEnterpriseContact>(`/ate/contacts/${id}`);
    const createAteEnterpriseContact = async (enterpriseId: string, data: Partial<AteEnterpriseContact>) =>
        sdk.fetchWrapper.post<AteEnterpriseContact>(`/ate/enterprises/${enterpriseId}/contacts`, data);
    const updateAteEnterpriseContact = async (id: string, data: Partial<AteEnterpriseContact>) =>
        sdk.fetchWrapper.put<void>(`/ate/contacts/${id}`, data);
    const archiveAteEnterpriseContact = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/contacts/${id}`);

    // Work Placements
    const getMyAteWorkPlacements = async () =>
        sdk.fetchWrapper.get<AteStudentStageSummary[]>(`/ate/mine/workplacements`);
    const getMyAteWorkPlacement = async (id: string) =>
        sdk.fetchWrapper.get<AteStudentStageDetail>(`/ate/mine/workplacements/${id}`);
    const createMyAteJournalEntry = async (id: string, data: AteStudentJournalRequest) =>
        sdk.fetchWrapper.post<AteStudentJournalEntry>(`/ate/mine/workplacements/${id}/journal`, data);
    const updateMyAteJournalEntry = async (workPlacementId: string, journalEntryId: string, data: AteStudentJournalRequest) =>
        sdk.fetchWrapper.put<AteStudentJournalEntry>(`/ate/mine/workplacements/${workPlacementId}/journal/${journalEntryId}`, data);
    const getAteWorkPlacement = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement>(`/ate/workplacements/${id}`);
    const getAteWorkPlacementsByFrequentation = async (frequentationId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement[]>(`/ate/frequentations/${frequentationId}/workplacements`);
    const getAteWorkPlacementsByGroup = async (groupId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement[]>(`/ate/groups/${groupId}/workplacements`);
    const getAteGroupDashboard = async (groupId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementDashboardItem[]>(`/ate/groups/${groupId}/dashboard`);
    // Establishment-wide (or site-/program-scoped) landing summary (#723): aggregated server-side,
    // unlike getAteGroupDashboard above which downloads the whole group and is filtered client-side.
    const getAteDashboardSummary = async (
        options: { siteId?: string; programId?: string; includeArchived?: boolean } = {}
    ) => {
        const params = new URLSearchParams();
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        // ALWAYS sent, never conditionally. The `if (x)` guard is right above, for optional
        // GUIDs — but on a boolean it swallows `false`, and the parameter is a non-nullable
        // `bool` on the server side: the request then went without it and came back as a 400,
        // so the page did not load at all. This is also the shape of the three other SDK calls
        // that carry a flag (`params.append(..., x.toString())`).
        params.set('includeArchived', String(options.includeArchived ?? false));
        const query = params.toString();
        return sdk.fetchWrapper.get<AteWorkPlacementDashboardSummary>(`/ate/dashboard/summary${query ? `?${query}` : ''}`);
    };
    const createAteWorkPlacement = async (workPlacement: Partial<AteWorkPlacement>, moduleIds: string[]) =>
        sdk.fetchWrapper.post<{ id: string }>(`/ate/workplacements`, { workPlacement, moduleIds });
    const updateAteWorkPlacement = async (id: string, data: Partial<AteWorkPlacement>) =>
        sdk.fetchWrapper.put<void>(`/ate/workplacements/${id}`, data);
    const archiveAteWorkPlacement = async (id: string) =>
        sdk.fetchWrapper.put<void>(`/ate/workplacements/${id}/archive`, {});
    const assignAteContactToWorkPlacement = async (id: string, contactId: string) =>
        sdk.fetchWrapper.post<void>(`/ate/workplacements/${id}/contacts/${contactId}`, {});
    const removeAteContactFromWorkPlacement = async (id: string, contactId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/workplacements/${id}/contacts/${contactId}`);

    // Work Placement Notes
    const getAteWorkPlacementNotes = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementNote[]>(`/ate/workplacements/${workPlacementId}/notes`);
    const createAteWorkPlacementNote = async (workPlacementId: string, data: Partial<AteWorkPlacementNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementNote>(`/ate/workplacements/${workPlacementId}/notes`, data);
    const updateAteWorkPlacementNote = async (id: string, data: Partial<AteWorkPlacementNote>) =>
        sdk.fetchWrapper.put<void>(`/ate/notes/${id}`, data);
    const deleteAteWorkPlacementNote = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/notes/${id}`);

    // Journal (Work Placement Activities)
    const getAteWorkPlacementActivities = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementActivity[]>(`/ate/workplacements/${workPlacementId}/activities`);
    const getAteWorkPlacementActivity = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementActivity>(`/ate/activities/${id}`);
    const createAteWorkPlacementActivity = async (workPlacementId: string, data: Partial<AteWorkPlacementActivity>) =>
        sdk.fetchWrapper.post<AteWorkPlacementActivity>(`/ate/workplacements/${workPlacementId}/activities`, data);
    const updateAteWorkPlacementActivity = async (id: string, data: Partial<AteWorkPlacementActivity>) =>
        sdk.fetchWrapper.put<void>(`/ate/activities/${id}`, data);
    const deleteAteWorkPlacementActivity = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activities/${id}`);
    const addAteActivityNote = async (activityId: string, data: Partial<AteWorkPlacementActivityNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementActivityNote>(`/ate/activities/${activityId}/notes`, data);
    const deleteAteActivityNote = async (noteId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activitynotes/${noteId}`);

    // Assessments
    const getAteActivityAssessments = async (activityId: string) =>
        sdk.fetchWrapper.get<AteActivityAssessment[]>(`/ate/activities/${activityId}/assessments`);
    const createAteActivityAssessment = async (activityId: string, data: Partial<AteActivityAssessment>) =>
        sdk.fetchWrapper.post<AteActivityAssessment>(`/ate/activities/${activityId}/assessments`, data);
    const updateAteActivityAssessment = async (id: string, data: Partial<AteActivityAssessment>) =>
        sdk.fetchWrapper.put<void>(`/ate/assessments/${id}`, data);
    const deleteAteActivityAssessment = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/assessments/${id}`);

    // Visits
    const getAteVisits = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementVisit[]>(`/ate/workplacements/${workPlacementId}/visits`);
    const getAteVisit = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementVisit>(`/ate/visits/${id}`);
    const createAteVisit = async (workPlacementId: string, data: Partial<AteWorkPlacementVisit>) =>
        sdk.fetchWrapper.post<AteWorkPlacementVisit>(`/ate/workplacements/${workPlacementId}/visits`, data);
    const updateAteVisit = async (id: string, data: Partial<AteWorkPlacementVisit>) =>
        sdk.fetchWrapper.put<void>(`/ate/visits/${id}`, data);
    const deleteAteVisit = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/visits/${id}`);
    const addAteVisitNote = async (visitId: string, data: Partial<AteWorkPlacementVisitNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementVisitNote>(`/ate/visits/${visitId}/notes`, data);
    const deleteAteVisitNote = async (noteId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/visitnotes/${noteId}`);

    // Questionnaires
    const getAteQuestionnaires = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaire[]>(`/ate/workplacements/${workPlacementId}/questionnaires`);
    const getAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.get<AteQuestionnaire>(`/ate/questionnaires/${id}`);
    const generateAteQuestionnaire = async (workPlacementId: string, templateId: string, visitId?: string | null, contactId?: string | null) =>
        sdk.fetchWrapper.post<AteQuestionnaire>(`/ate/workplacements/${workPlacementId}/questionnaires/generate`, { templateId, visitId, contactId });
    const sendAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaires/${id}/send`, {});
    const respondToAteQuestionnaireItem = async (itemId: string, content: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaireitems/${itemId}/respond`, { content });
    const completeAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaires/${id}/complete`, {});

    // HAC
    const getAteHACs = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteHAC[]>(`/ate/workplacements/${workPlacementId}/hacs`);
    const getAteHAC = async (id: string) =>
        sdk.fetchWrapper.get<AteHAC>(`/ate/hacs/${id}`);
    const createAteHAC = async (workPlacementId: string, templateId: string) =>
        sdk.fetchWrapper.post<AteHAC>(`/ate/workplacements/${workPlacementId}/hacs`, { templateId });
    const upsertAteHACEntry = async (hacId: string, data: Partial<AteHACEntry>) =>
        sdk.fetchWrapper.post<AteHACEntry>(`/ate/hacs/${hacId}/entries`, data);
    const deleteAteHACEntry = async (entryId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hac-entries/${entryId}`);

    // Scheduling / Simulation
    /**
     * Asks the server to halt the running solve identified by `solveId`. The
     * solver returns its latest incumbent through the still-open SSE stream.
     */
    const cancelScheduling = async (solveId: string) =>
        sdk.fetchWrapper.post<void>(`/scheduling-v2/cancel/${solveId}`, {});

    /**
     * Starts a group solve in the background and returns the id to follow it by. Progress and the
     * final result arrive on the permanent SSE channel under `scheduling.groups` — see `runJob`.
     * The backend rebuilds the V2 request from the groupIds, solves, and translates the result back
     * to the legacy SchedulingSolverResponse shape so simulation.vue consumes it unchanged.
     */
    const solveGroupSchedulingAsync = async (data: SolveGroupsRequest) =>
        sdk.fetchWrapper.post<{ jobId: string }>(`/Plannings/Solve-Groups-Async`, data);

    /**
     * Atomically replaces the slots of several plannings in one transaction.
     * Stateless apply path for a chosen simulation solution: the client sends
     * the slots it already holds from the rendered solution.
     */
    const applyPlanningSlotsBatch = async (data: ApplyPlanningSlotsRequest) =>
        sdk.fetchWrapper.put<ApplyPlanningSlotsResponse>(`/Plannings/Slots/Batch`, data);

    // Scheduling V2 (slot-based solver)
    const solveSchedulingV2 = async (data: SchedulingV2Request) =>
        sdk.fetchWrapper.post<SchedulingV2Response>(`/scheduling-v2/solve`, data, { errorHandler: null });
    const cancelSchedulingV2 = async (solveId: string) =>
        sdk.fetchWrapper.post<void>(`/scheduling-v2/cancel/${solveId}`, {});
    /**
     * Starts a solve in the background and returns the id to follow it by. Progress and the final
     * result arrive on the permanent SSE channel under `scheduling.progress` — see `runJob`.
     */
    const solveSchedulingV2Async = async (data: SchedulingV2Request) =>
        sdk.fetchWrapper.post<{ jobId: string }>(`/scheduling-v2/solve-async`, data, { errorHandler: null });
    const getSchedulingV2SampleRequest = async (preset: DatasetPresetV2, seed = 42) =>
        sdk.fetchWrapper.get<SchedulingV2Request>(`/scheduling-v2/sample-request?preset=${preset}&seed=${seed}`);

    // ── Student submissions (Arkiv, LOT3 #629/#634) ──────────────────────────
    // JSON calls of the frozen contract (StudentSubmissionsRouteDto.cs): the bytes NEVER
    // go through here — uploading/downloading contents happens through the fondation proxy
    // /api/core/proxy/arkiv/documents/{arkivDocumentId}/versions[...] (see utils/arkiv.ts
    // and the use-submission-upload composable).

    // Student routes (self-service "Mine")
    /** Starts an absence justification: range, comment, file descriptors. */
    const createMyAbsenceJustification = async (frequentationId: string, data: CreateAbsenceJustificationRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<CreateSubmissionResponse>(`/frequentations/mine/${frequentationId}/absenceJustifications`, data, options);
    /** Initiation d'une remise d'examen. */
    const createMyExamSubmission = async (frequentationId: string, examId: string, data: CreateExamSubmissionRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<CreateSubmissionResponse>(`/frequentations/mine/${frequentationId}/exams/${examId}/submissions`, data, options);
    /** Confirms the submission once the bytes are uploaded to Arkiv (Status Pending → Ready). */
    const confirmMySubmission = async (submissionId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<StudentSubmissionResponse>(`/submissions/mine/${submissionId}/confirm`, {}, options);
    /** My submissions for a frequentation, optional filter by kind. */
    const getMyFrequentationSubmissions = async (frequentationId: string, kind?: SubmissionKind) =>
        sdk.fetchWrapper.get<StudentSubmissionsResponse>(`/frequentations/mine/${frequentationId}/submissions${kind ? `?kind=${kind}` : ''}`);
    /** Adds files to an existing submission. */
    const addMySubmissionFiles = async (submissionId: string, data: AddSubmissionFilesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<AddSubmissionFilesResponse>(`/submissions/mine/${submissionId}/files`, data, options);
    /** Removes a file from a submission. */
    const removeMySubmissionFile = async (submissionId: string, fileId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/submissions/mine/${submissionId}/files/${fileId}`, options);
    /** Deletes the submission (and its Arkiv documents). */
    const deleteMySubmission = async (submissionId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/submissions/mine/${submissionId}`, options);

    // Staff routes (local reads — they answer even if Arkiv is down)
    /** Every submission of a frequentation. */
    const getFrequentationSubmissions = async (frequentationId: string) =>
        sdk.fetchWrapper.get<StudentSubmissionsResponse>(`/frequentations/${frequentationId}/submissions`);
    /** Batch for the assiduity grid: submissions covering the given presences. */
    const getSubmissionsByPresences = async (presenceIds: string[]) =>
        sdk.fetchWrapper.get<PresenceSubmissionsResponse>(`/presences/submissions?presenceIds=${presenceIds.map(encodeURIComponent).join(',')}`);
    /** Every submission of a student (all frequentations). */
    const getSubmissionsByStudent = async (studentId: string) =>
        sdk.fetchWrapper.get<StudentSubmissionsResponse>(`/students/${studentId}/submissions`);

    // Initialization status
    const getInitializationStatus = async () => sdk.fetchWrapper.get<InitializationStatusResponse>(`/initializationStatus`);

    return {
        learningPrograms,
        availableLearningPrograms,
        getLearningProgram,
        addLearningProgram,
        updateLearningProgram,
        setLearningProgramAvailability,
        cloneLearningProgram,
        learningModules,
        learningModulesByProgram,
        getAvailableLearningModules,
        getLearningModule,
        addLearningModule,
        updateLearningModule,
        archiveLearningModule,
        restoreLearningModule,
        archivedLearningModulesByProgram,
        exportLearningData,
        importLearningData,
        teachers,
        getTeacher,
        teachersPaged,
        getCurrentTeacher,
        getTeacherPlanning,
        getTeacherArchiveImpact,
        getTeacherTransferInventory,
        archiveTeacherWithTransfer,
        restoreTeacher,
        getTeachersCurrentActivity,
        getGroupPlanning,
        isTeacherGroupMappedToEntraId,
        teachersByProgram,
        getAvailableTeachers,
        addTeacher,
        addTeachersFromEntraIdGroup,
        archiveTeacher,
        setTeacherColor,
        getTeacherLearningPrograms,
        assignTeacherToProgram,
        removeTeacherFromProgram,
        getTeacherLearningModules,
        setTeacherProgramModules,
        getTeacherUnavailableRanges,
        getTeacherWorkAvailability,
        getTeacherAvailabilities,
        createTeacherAvailability,
        updateTeacherAvailability,
        deleteTeacherAvailability,
        frequentations,
        myFrequentations,
        myFrequentationPresences,
        myFrequentationLearningModules,
        myFrequentationModulePlans,
        generateMyStudentScheduleReport,
        generateMyStudentCalendarReport,
        generateMyProgressionReport,
        generateMyAbsenceReport,
        myBulkUpsertPresences,
        getFrequentationById,
        getScheduleChainInfo,
        applyScheduleChainOperation,
        frequentationsByTeacher,
        frequentationsByProgram,
        getStudentById,
        getStudentByUserId,
        frequentationsByStudent,
        addFrequentation,
        addFrequentationsFromEntraIdGroup,
        updateFrequentation,
        updateFrequentationSecondaryStaff,
        getSecondaryStaffRoles,
        frequentationsPaged,
        getFrequentationSummary,
        updateStudentCoordinates,
        getStudentAddresses,
        createStudentAddress,
        updateStudentAddress,
        deleteStudentAddress,
        searchStudentsWithFrequentation,
        searchEnrollableUsers,
        searchStudents,
        studentsPaged,
        addStudent,
        archiveStudent,
        restoreStudent,
        addPresence,
        updatePresence,
        deletePresence,
        bulkUpsertPresences,
        getPresencesByDateRange,
        getTeacherPresencesByDateRange,
        getPresenceSummary,
        getTeacherLoadSummary,
        getStudentNotesByPresence,
        getStudentNotes,
        createStudentNoteByPresence,
        createStudentNote,
        updateStudentNote,
        deleteStudentNote,
        getStudentSharedNotes,
        createStudentSharedNote,
        updateStudentSharedNote,
        deleteStudentSharedNote,
        mySharedNotes,
        exams,
        examsFiltered,
        myExams,
        examsCount,
        getExam,
        getExamsByFrequentation,
        createExam,
        updateExam,
        deleteExam,
        getConsolidatedView,
        generateExamAssignmentReport,
        generateAbsenceReport,
        generateWeeklyPresenceReport,
        generateProgressionReport,
        generateResultTransmissionModuleReport,
        generateResultTransmissionAllModulesReport,
        generateDepartureSheetReport,
        generateReturnSheetReport,
        generateRetakeRefusalReport,
        generateFrequentationInfoReport,
        generateStudentScheduleReport,
        generateAtRiskReport,
        generateModulePerformanceReport,
        generateTeacherWorkloadReport,
        generateTeacherAnnualLoadReport,
        generateTeacherAssignmentCalendarReport,
        generateCalendarYearReport,
        generateAttendanceTrendReport,
        generateCumulativeHoursReport,
        generateWorkStudyTimeReport,
        getStudentJourney,
        getFrequentationModuleResults,
        getFrequentationModuleStatuses,
        setModuleStatus,
        deleteModuleStatus,
        getMyFrequentationModuleLocks,
        getFrequentationModuleLocks,
        setFrequentationModuleLock,
        setAllFrequentationModuleLocks,
        getModulePlan,
        getModulePlansBatch,
        getScheduleAxis,
        getTeacherModulePlans,
        getTeacherDaySheet,
        getProgramDaySheet,
        notifyUnchosenPeriod,
        getTeacherDailyReport,
        getStudentsWithRecentAbsences,
        getStudentsExceedingModuleTime,
        getStudentsWithDataIssues,
        getCssPresenceStats,
        getPlannings,
        getPlanning,
        createPlanning,
        updatePlanning,
        deletePlanning,
        groups,
        getActiveGroups,
        getGroup,
        createGroup,
        duplicateGroup,
        updateGroup,
        deleteGroup,
        // Site (Centre) functions
        getSites,
        getExamLocations,
        createExamLocation,
        updateExamLocation,
        archiveExamLocation,
        getSite,
        createSite,
        updateSite,
        getSiteLogo,
        uploadSiteLogo,
        deleteSiteLogo,
        setProgramSites,
        importGricsCentres,
        importGricsTeachers,
        importGricsStudents,
        importGricsFrequentations,
        previewGricsStudents,
        previewGricsFrequentations,
        // Room functions
        getRooms,
        getRoomArchiveImpact,
        getRoomsBySite,
        getRoom,
        createRoom,
        updateRoom,
        // Resource functions
        getResources,
        getResourcesBySite,
        getResource,
        createResource,
        updateResource,
        setResourcePrograms,
        // UserPreference functions
        getUserPreferences,
        updateUserPreferences,
        // FrequentationTag functions
        getFrequentationTags,
        createFrequentationTag,
        updateFrequentationTag,
        deleteFrequentationTag,
        addFrequentationTagValue,
        updateFrequentationTagValue,
        deleteFrequentationTagValue,
        getTagsByStudent,
        setStudentTags,
        // FrequentationNoteTypeDefinition functions
        getFrequentationNoteTypeDefinitions,
        createFrequentationNoteTypeDefinition,
        updateFrequentationNoteTypeDefinition,
        deleteFrequentationNoteTypeDefinition,
        reorderFrequentationNoteTypeDefinitions,
        // PresenceTypeDefinition functions
        getPresenceTypeDefinitions,
        createPresenceTypeDefinition,
        updatePresenceTypeDefinition,
        deletePresenceTypeDefinition,
        reorderPresenceTypeDefinitions,
        // PresenceStatusDefinition functions
        getPresenceStatusDefinitions,
        createPresenceStatusDefinition,
        updatePresenceStatusDefinition,
        deletePresenceStatusDefinition,
        reorderPresenceStatusDefinitions,
        getGroupMembers,
        getAvailableGroupMembers,
        addGroupMember,
        removeGroupMember,
        importJadeRoster,
        importAdmissionFpRoster,
        importIndividualJadeRoster,
        importIndividualAdmissionFpRoster,
        getProgramModuleSequences,
        getProgramPhaseModuleSequences,
        updateProgramPhaseModuleSequences,
        getProgramPhases,
        updateProgramPhases,
        deleteProgramPhase,
        getProgramGroupMembers,
        getGroupStats,
        getGroupDashboardStats,
        getPlanningPhases,
        updatePlanningPhases,
        deletePlanningPhase,
        getPlanningModuleSequences,
        getPlanningPhaseModuleSequences,
        updatePlanningPhaseModuleSequences,
        getPlanningSlots,
        updatePlanningSlots,
        patchPlanningSlot,
        patchPlanningSlots,
        findSlotAssignmentConflicts,
        findSlotAssignmentBusy,
        clearSlotAssignments,
        regeneratePlanningSlots,
        getEffectivePlan,
        // Individual Planning
        createIndividualPlanning,
        deleteIndividualPlanning,
        getIndividualPlanningsByProgram,
        // ATE
        ATE_MODULE,
        ATE_PERMISSION_ADMINISTRATOR,
        ATE_PERMISSION_TEACHER,
        ATE_PERMISSION_SUPERVISOR,
        ATE_PERMISSION_VIEWER,
        getAteRatingGroups,
        createAteRatingGroup,
        updateAteRatingGroup,
        archiveAteRatingGroup,
        getAteActivityTemplates,
        createAteActivityTemplate,
        updateAteActivityTemplate,
        archiveAteActivityTemplate,
        getAteQuestionnaireTemplates,
        createAteQuestionnaireTemplate,
        updateAteQuestionnaireTemplate,
        archiveAteQuestionnaireTemplate,
        getArchivedAteQuestionnaireTemplates,
        restoreAteQuestionnaireTemplate,
        createAteQuestionnaireTemplateItem,
        updateAteQuestionnaireTemplateItem,
        deleteAteQuestionnaireTemplateItem,
        getAteHACTemplates,
        createAteHACTemplate,
        updateAteHACTemplate,
        archiveAteHACTemplate,
        createAteHACTemplateItem,
        updateAteHACTemplateItem,
        deleteAteHACTemplateItem,
        getAteEnterprises,
        getAteEnterprise,
        createAteEnterprise,
        updateAteEnterprise,
        archiveAteEnterprise,
        getAteEnterpriseContacts,
        getAteEnterpriseContact,
        createAteEnterpriseContact,
        updateAteEnterpriseContact,
        archiveAteEnterpriseContact,
        getAteWorkPlacement,
        getMyAteWorkPlacements,
        getMyAteWorkPlacement,
        createMyAteJournalEntry,
        updateMyAteJournalEntry,
        getAteWorkPlacementsByFrequentation,
        getAteWorkPlacementsByGroup,
        getAteGroupDashboard,
        getAteDashboardSummary,
        createAteWorkPlacement,
        updateAteWorkPlacement,
        archiveAteWorkPlacement,
        assignAteContactToWorkPlacement,
        removeAteContactFromWorkPlacement,
        getAteWorkPlacementNotes,
        createAteWorkPlacementNote,
        updateAteWorkPlacementNote,
        deleteAteWorkPlacementNote,
        getAteWorkPlacementActivities,
        getAteWorkPlacementActivity,
        createAteWorkPlacementActivity,
        updateAteWorkPlacementActivity,
        deleteAteWorkPlacementActivity,
        addAteActivityNote,
        deleteAteActivityNote,
        getAteActivityAssessments,
        createAteActivityAssessment,
        updateAteActivityAssessment,
        deleteAteActivityAssessment,
        getAteVisits,
        getAteVisit,
        createAteVisit,
        updateAteVisit,
        deleteAteVisit,
        addAteVisitNote,
        deleteAteVisitNote,
        getAteQuestionnaires,
        getAteQuestionnaire,
        generateAteQuestionnaire,
        sendAteQuestionnaire,
        respondToAteQuestionnaireItem,
        completeAteQuestionnaire,
        getAteHACs,
        getAteHAC,
        createAteHAC,
        upsertAteHACEntry,
        deleteAteHACEntry,
        PERMISSION_ADMINISTRATOR,
        PERMISSION_SUPERVISOR,
        PERMISSION_TEACHER,
        PERMISSION_EXTENDED_TEACHER,
        PERMISSION_ARCHIVE_STUDENTS,
        PERMISSION_VIEWER,
        calendarSdk: sdk.Calendar,
        schedulePatternSdk: sdk.SchedulePattern,
        scheduleAssignmentSdk: sdk.ScheduleAssignment,
        // Demonstration
        DEMO_MODULE,
        DEMO_PERMISSION_ADMINISTRATOR,
        DEMO_PERMISSION_DESIGNER,
        DEMO_PERMISSION_PRESENTER,
        DEMO_PERMISSION_OBSERVER,
        getDemonstrationConfigurations,
        getDemonstrationConfiguration,
        getRecommendedDemonstrationConfiguration,
        createDemonstrationConfiguration,
        updateDemonstrationConfiguration,
        deleteDemonstrationConfiguration,
        duplicateDemonstrationConfiguration,
        generateDemoStudents,
        generateDemoTeachers,
        applyDemonstrationConfiguration,
        applyDemonstrationConfigAsync,
        removeDemonstrationConfiguration,
        cleanupDemoData,
        getDemoDataStats,
        // Scheduling / Simulation
        solveGroupSchedulingAsync,
        startSchedulingCertification,
        getSchedulingCertification,
        startSchedulingAnalysis,
        getSchedulingAnalysis,
        applyPlanningSlotsBatch,
        cancelScheduling,
        // Scheduling V2
        solveSchedulingV2,
        cancelSchedulingV2,
        solveSchedulingV2Async,
        getSchedulingV2SampleRequest,
        // Student submissions (Arkiv)
        createMyAbsenceJustification,
        createMyExamSubmission,
        confirmMySubmission,
        getMyFrequentationSubmissions,
        addMySubmissionFiles,
        removeMySubmissionFile,
        deleteMySubmission,
        getFrequentationSubmissions,
        getSubmissionsByPresences,
        getSubmissionsByStudent,
        // Initialization
        getInitializationStatus,
        getVacancies,
        createVacancy,
        archiveVacancy,
        hireVacancy,
        renameVacancy,
    }
}
